/**
 * Dual Write Test Screen
 *
 * A debug screen to run sales and supplies dual-write tests and verify
 * Firestore-Supabase synchronization.
 */

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Alert,
  Platform,
} from 'react-native';
import { useTheme } from '@ui-kitten/components';
import * as Clipboard from 'expo-clipboard';
import { useAtomValue } from 'jotai';
import { useRoute } from '@react-navigation/native';
import { organizationAtom, dbAtom, storesDictAtom, productsDictAtom, constantsAtom, userIdAtom } from '../../global/AtomsProvider';
import {
  runSalesDualWriteTests,
  runQuickSmokeTest,
  runCascadingSalesTests,
  TEST_VARIATIONS,
  CASCADING_TEST_VARIATIONS as SALES_CASCADING_VARIATIONS,
} from '../../utils/salesDualWriteTest';
import {
  runSuppliesDualWriteTests,
  runQuickSuppliesSmokeTest,
  runCascadingSuppliesTests,
  SUPPLY_TEST_VARIATIONS,
  CASCADING_TEST_VARIATIONS,
} from '../../utils/suppliesDualWriteTest';
import {
  runConstantsDualWriteTests,
  runQuickConstantsSmokeTest,
  compareAllConstants,
  CONSTANTS_TEST_VARIATIONS,
} from '../../utils/constantsDualWriteTest';
import {
  runOrganizationDualWriteTests,
  runQuickOrganizationSmokeTest,
  ORGANIZATION_TEST_VARIATIONS,
} from '../../utils/organizationDualWriteTest';
import {
  runProductsDualWriteTests,
  runQuickProductsSmokeTest,
  PRODUCT_TEST_VARIATIONS,
} from '../../utils/productsDualWriteTest';
import {
  runProductCategoriesDualWriteTests,
  runQuickProductCategoriesSmokeTest,
  PRODUCT_CATEGORY_TEST_VARIATIONS,
} from '../../utils/productCategoriesDualWriteTest';
import {
  runSuppliersDualWriteTests,
  runQuickSuppliersSmokeTest,
  SUPPLIER_TEST_VARIATIONS,
} from '../../utils/suppliersDualWriteTest';
import {
  runStoresDualWriteTests,
  runQuickStoresSmokeTest,
  STORE_TEST_VARIATIONS,
} from '../../utils/storesDualWriteTest';
import {
  runUsersDualWriteTests,
  runQuickUsersSmokeTest,
  USER_TEST_VARIATIONS,
} from '../../utils/usersDualWriteTest';
import {
  runFullTransactionTest,
  TRANSACTION_TEST_VARIATIONS,
  getTransactionTestCategories,
} from '../../utils/transactionsDualWriteTest';
import {
  runFullReturnTest,
  RETURN_TEST_VARIATIONS,
  getReturnTestCategories,
} from '../../utils/returnsDualWriteTest';
import {
  runFullTransferTest,
  TRANSFER_TEST_VARIATIONS,
  getTransferTestCategories,
} from '../../utils/transfersDualWriteTest';
import {
  runFullRevisionTest,
  REVISION_TEST_VARIATIONS,
  getRevisionTestCategories,
} from '../../utils/revisionsDualWriteTest';
import {
  runFullWriteoffTest,
  WRITEOFF_TEST_VARIATIONS,
  getWriteoffTestCategories,
} from '../../utils/writeoffsDualWriteTest';
import {
  runFullSpendingTest,
  SPENDING_TEST_VARIATIONS,
  getSpendingTestCategories,
} from '../../utils/spendingsDualWriteTest';
import {
  runFullClientTest,
  CLIENT_TEST_VARIATIONS,
  getClientTestCategories,
} from '../../utils/clientsDualWriteTest';
import {
  runFullManufactureTest,
  MANUFACTURE_TEST_VARIATIONS,
  getManufactureTestCategories,
} from '../../utils/manufacturesDualWriteTest';
import {
  runFullWorkingHoursTest,
  WORKING_HOURS_TEST_VARIATIONS,
  getWorkingHoursTestCategories,
} from '../../utils/workingHoursDualWriteTest';
import {
  runFullCashboxTransactionsTest,
  CASHBOX_TRANSACTIONS_TEST_VARIATIONS,
  getCashboxTransactionsTestCategories,
} from '../../utils/cashboxTransactionsDualWriteTest';
import {
  runFullSavedCartsTest,
  SAVED_CARTS_TEST_VARIATIONS,
  getSavedCartsTestCategories,
} from '../../utils/savedCartsDualWriteTest';
import {
  runFullSupplyOrdersTest,
  SUPPLY_ORDERS_TEST_VARIATIONS,
  getSupplyOrdersTestCategories,
} from '../../utils/supplyOrdersDualWriteTest';
import {
  runFullChecklistsTest,
  CHECKLISTS_TEST_VARIATIONS,
  getChecklistsTestCategories,
} from '../../utils/checklistsDualWriteTest';
import {
  runFullPrintTemplatesTest,
  PRINT_TEMPLATES_TEST_VARIATIONS,
  getPrintTemplatesTestCategories,
} from '../../utils/printTemplatesDualWriteTest';
import {
  runStatsDualWriteTests,
  runQuickStatsSmokeTest,
  STATS_TEST_VARIATIONS,
} from '../../utils/statsDualWriteTest';

const TABS = {
  SALES: 'sales',
  SUPPLIES: 'supplies',
  PRODUCTS: 'products',
  CATEGORIES: 'categories',
  SUPPLIERS: 'suppliers',
  STORES: 'stores',
  USERS: 'users',
  TRANSACTIONS: 'transactions',
  RETURNS: 'returns',
  TRANSFERS: 'transfers',
  REVISIONS: 'revisions',
  WRITEOFFS: 'writeoffs',
  SPENDINGS: 'spendings',
  CLIENTS: 'clients',
  MANUFACTURES: 'manufactures',
  WORKING_HOURS: 'workingHours',
  CASHBOX: 'cashbox',
  SAVED_CARTS: 'savedCarts',
  SUPPLY_ORDERS: 'supplyOrders',
  CHECKLISTS: 'checklists',
  PRINT_TEMPLATES: 'printTemplates',
  CONSTANTS: 'constants',
  ORGANIZATIONS: 'organizations',
  STATS: 'stats',
};

// Get backend API URL from environment
const API_BASE_URL = process.env.EXPO_PUBLIC_BACKEND_API_URL || 'http://localhost:3000/api';

// Generate a unique session ID
const generateSessionId = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

const DualWriteTestScreen = () => {
  const route = useRoute();
  const organization = useAtomValue(organizationAtom);
  const db = useAtomValue(dbAtom);
  const storesDict = useAtomValue(storesDictAtom);
  const productsDict = useAtomValue(productsDictAtom);
  const constants = useAtomValue(constantsAtom);
  const theme = useTheme();

  // Get initial tab from route params (supports 'sales', 'supplies', 'products', 'categories', 'suppliers', 'stores', 'users', 'transactions', 'returns', 'transfers', 'revisions', 'writeoffs', 'constants', or 'organizations')
  const initialTab = route.params?.tab === 'supplies' ? TABS.SUPPLIES :
                     route.params?.tab === 'products' ? TABS.PRODUCTS :
                     route.params?.tab === 'categories' ? TABS.CATEGORIES :
                     route.params?.tab === 'suppliers' ? TABS.SUPPLIERS :
                     route.params?.tab === 'stores' ? TABS.STORES :
                     route.params?.tab === 'users' ? TABS.USERS :
                     route.params?.tab === 'transactions' ? TABS.TRANSACTIONS :
                     route.params?.tab === 'returns' ? TABS.RETURNS :
                     route.params?.tab === 'transfers' ? TABS.TRANSFERS :
                     route.params?.tab === 'revisions' ? TABS.REVISIONS :
                     route.params?.tab === 'writeoffs' ? TABS.WRITEOFFS :
                     route.params?.tab === 'spendings' ? TABS.SPENDINGS :
                     route.params?.tab === 'clients' ? TABS.CLIENTS :
                     route.params?.tab === 'manufactures' ? TABS.MANUFACTURES :
                     route.params?.tab === 'workingHours' ? TABS.WORKING_HOURS :
                     route.params?.tab === 'cashbox' ? TABS.CASHBOX :
                     route.params?.tab === 'savedCarts' ? TABS.SAVED_CARTS :
                     route.params?.tab === 'supplyOrders' ? TABS.SUPPLY_ORDERS :
                     route.params?.tab === 'checklists' ? TABS.CHECKLISTS :
                     route.params?.tab === 'printTemplates' ? TABS.PRINT_TEMPLATES :
                     route.params?.tab === 'constants' ? TABS.CONSTANTS :
                     route.params?.tab === 'organizations' ? TABS.ORGANIZATIONS :
                     route.params?.tab === 'stats' ? TABS.STATS : TABS.SALES;

  // Tab state
  const [activeTab, setActiveTab] = useState(initialTab);

  // Test state
  const [isRunning, setIsRunning] = useState(false);
  const [runningTestType, setRunningTestType] = useState(null); // 'quick', 'full', 'specific', or 'category'
  const [runningTestId, setRunningTestId] = useState(null); // specific test being run
  const [runningCategory, setRunningCategory] = useState(null); // category being run
  const [results, setResults] = useState(null);
  const [logs, setLogs] = useState([]);
  const [sseConnected, setSseConnected] = useState(false);
  const eventSourceRef = useRef(null);
  const sessionIdRef = useRef(null);

  // Sales category selection
  const [selectedSalesCategories, setSelectedSalesCategories] = useState({
    client: true,
    payment: true,
    cart: true,
    discount: true,
    purchaseMethod: true,
    delivery: true,
    optional: true,
    date: true,
    lifecycle: true,
    cascading: true, // P0: Cascading write verifications
  });

  // Supplies category selection
  const [selectedSuppliesCategories, setSelectedSuppliesCategories] = useState({
    items: true,
    payment: true,
    supplier: true,
    optional: true,
    date: true,
    lifecycle: true,
    cascading: true, // P0: Cascading write verifications
  });

  // Constants category selection
  const [selectedConstantsCategories, setSelectedConstantsCategories] = useState({
    wallets: true,
    payment_methods: true,
    roles: true,
    product_units: true,
    spending_categories: true,
    discount_options: true,
    write_off_reasons: true,
    return_reasons: true,
    purchase_methods: true,
  });

  // Organization category selection
  const [selectedOrganizationCategories, setSelectedOrganizationCategories] = useState({
    modules: true,
    marketplace: true,
    link_settings: true,
    stock_settings: true,
    basic_info: true,
    currency: true,
  });

  // Products category selection
  const [selectedProductsCategories, setSelectedProductsCategories] = useState({
    basic: true,
    relations: true,
    pricing: true,
    types: true,
    inventory: true,
    lifecycle: true,
  });

  // Product Categories category selection
  const [selectedCategoriesCategories, setSelectedCategoriesCategories] = useState({
    basic: true,
    lifecycle: true,
  });

  // Suppliers category selection
  const [selectedSuppliersCategories, setSelectedSuppliersCategories] = useState({
    basic: true,
    lifecycle: true,
  });

  // Stores category selection
  const [selectedStoresCategories, setSelectedStoresCategories] = useState({
    basic: true,
    lifecycle: true,
  });

  // Users category selection
  const [selectedUsersCategories, setSelectedUsersCategories] = useState({
    basic: true,
    store: true,
  });

  // Transactions category selection
  const [selectedTransactionsCategories, setSelectedTransactionsCategories] = useState({
    create: true,
    delete: true,
  });

  // Returns category selection
  const [selectedReturnsCategories, setSelectedReturnsCategories] = useState({
    create: true,
    delete: true,
  });

  // Transfers category selection
  const [selectedTransfersCategories, setSelectedTransfersCategories] = useState({
    create: true,
    delete: true,
  });

  // Revisions category selection
  const [selectedRevisionsCategories, setSelectedRevisionsCategories] = useState({
    create: true,
    delete: true,
  });

  // Writeoffs category selection
  const [selectedWriteoffsCategories, setSelectedWriteoffsCategories] = useState({
    create: true,
    delete: true,
  });

  // Spendings category selection
  const [selectedSpendingsCategories, setSelectedSpendingsCategories] = useState({
    create: true,
    delete: true,
  });

  // Clients category selection
  const [selectedClientsCategories, setSelectedClientsCategories] = useState({
    create: true,
    update: true,
    delete: true,
  });

  // Manufactures category selection
  const [selectedManufacturesCategories, setSelectedManufacturesCategories] = useState({
    create: true,
    delete: true,
  });

  // Working Hours category selection
  const [selectedWorkingHoursCategories, setSelectedWorkingHoursCategories] = useState({
    create: true,
    update: true,
    delete: true,
  });

  // Cashbox Transactions category selection
  const [selectedCashboxCategories, setSelectedCashboxCategories] = useState({
    create: true,
    update: true,
    delete: true,
  });

  // Saved Carts category selection
  const [selectedSavedCartsCategories, setSelectedSavedCartsCategories] = useState({
    create: true,
    update: true,
    delete: true,
  });

  // Supply Orders category selection
  const [selectedSupplyOrdersCategories, setSelectedSupplyOrdersCategories] = useState({
    create: true,
    update: true,
    delete: true,
  });

  // Checklists category selection
  const [selectedChecklistsCategories, setSelectedChecklistsCategories] = useState({
    create: true,
    update: true,
  });

  // Print Templates category selection
  const [selectedPrintTemplatesCategories, setSelectedPrintTemplatesCategories] = useState({
    create: true,
    update: true,
    delete: true,
  });

  // Stats category selection
  const [selectedStatsCategories, setSelectedStatsCategories] = useState({
    daily_stats: true,
    product_stats: true,
  });

  // Initialize all categories as expanded by default
  const [expandedCategories, setExpandedCategories] = useState({
    // Sales categories
    'sales-client': true,
    'sales-payment': true,
    'sales-cart': true,
    'sales-discount': true,
    'sales-purchaseMethod': true,
    'sales-delivery': true,
    'sales-optional': true,
    'sales-date': true,
    'sales-lifecycle': true,
    'sales-cascading': true, // P0: Cascading write verifications
    // Supplies categories
    'supplies-items': true,
    'supplies-payment': true,
    'supplies-supplier': true,
    'supplies-optional': true,
    'supplies-date': true,
    'supplies-lifecycle': true,
    'supplies-cascading': true, // P0: Cascading write verifications
    // Constants categories
    'constants-wallets': true,
    'constants-payment_methods': true,
    'constants-roles': true,
    'constants-product_units': true,
    'constants-spending_categories': true,
    'constants-discount_options': true,
    'constants-write_off_reasons': true,
    'constants-return_reasons': true,
    'constants-purchase_methods': true,
    // Organization categories
    'organizations-modules': true,
    'organizations-marketplace': true,
    'organizations-link_settings': true,
    'organizations-stock_settings': true,
    'organizations-basic_info': true,
    'organizations-currency': true,
    // Products categories
    'products-basic': true,
    'products-relations': true,
    'products-pricing': true,
    'products-types': true,
    'products-inventory': true,
    'products-lifecycle': true,
    // Product Categories categories
    'categories-basic': true,
    'categories-lifecycle': true,
    // Suppliers categories
    'suppliers-basic': true,
    'suppliers-lifecycle': true,
    // Stores categories
    'stores-basic': true,
    'stores-lifecycle': true,
    // Users categories
    'users-basic': true,
    'users-store': true,
    // Transactions categories
    'transactions-create': true,
    'transactions-delete': true,
    // Returns categories
    'returns-create': true,
    'returns-delete': true,
    // Transfers categories
    'transfers-create': true,
    'transfers-delete': true,
    // Revisions categories
    'revisions-create': true,
    'revisions-delete': true,
    // Writeoffs categories
    'writeoffs-create': true,
    'writeoffs-delete': true,
    // Spendings categories
    'spendings-create': true,
    'spendings-delete': true,
    // Clients categories
    'clients-create': true,
    'clients-update': true,
    'clients-delete': true,
    // Manufactures categories
    'manufactures-create': true,
    'manufactures-delete': true,
    // Supply Orders categories
    'supplyOrders-create': true,
    'supplyOrders-update': true,
    'supplyOrders-delete': true,
    // Checklists categories
    'checklists-create': true,
    'checklists-update': true,
    // Print Templates categories
    'printTemplates-create': true,
    'printTemplates-update': true,
    'printTemplates-delete': true,
  });

  // Compute test configuration info (mirrors what the test utility uses)
  const testConfig = useMemo(() => {
    const storeIds = Object.keys(storesDict || {});
    const storeId = storeIds[0];
    const store = storeId ? storesDict[storeId] : null;
    const products = Object.values(productsDict || {});

    // Payment methods are stored in constants, NOT on organization
    const allPaymentMethods = constants?.paymentMethods?.data || [];
    const paymentMethods = allPaymentMethods.filter(p =>
      p.destinationWalletForStore && Object.keys(p.destinationWalletForStore).length > 0
    );

    // Debug: check what store IDs are configured in payment methods
    const configuredStoreIds = new Set();
    allPaymentMethods.forEach(p => {
      if (p.destinationWalletForStore) {
        Object.keys(p.destinationWalletForStore).forEach(id => configuredStoreIds.add(id));
      }
    });

    return {
      storeName: store?.name || storeId || 'No store',
      storeId,
      storeCount: storeIds.length,
      productCount: products.length,
      paymentMethodCount: paymentMethods.length,
      totalPaymentMethods: allPaymentMethods.length,
      hasValidPaymentMethods: paymentMethods.length > 0,
      organizationName: organization?.name || organization?.id || 'Unknown',
      configuredStoreIds: Array.from(configuredStoreIds),
    };
  }, [storesDict, productsDict, organization, constants]);

  const addLog = useCallback((message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, message, type }]);
  }, []);

  // Connect to SSE for real-time backend logs
  const connectSSE = useCallback(() => {
    // Only available on web platform (EventSource is a browser API)
    if (Platform.OS !== 'web') {
      console.log('SSE not available on native platform');
      return null;
    }

    const sessionId = generateSessionId();
    sessionIdRef.current = sessionId;

    try {
      const eventSource = new EventSource(`${API_BASE_URL}/logs/stream?session=${sessionId}`);

      eventSource.onopen = () => {
        setSseConnected(true);
        addLog('[SSE] Connected to backend log stream', 'info');
      };

      eventSource.onmessage = (event) => {
        try {
          const log = JSON.parse(event.data);
          const level = log.level === 'error' ? 'error' : log.level === 'warn' ? 'warning' : 'info';
          addLog(`[Backend] ${log.message}`, level);
        } catch (e) {
          console.warn('Failed to parse SSE message:', e);
        }
      };

      eventSource.onerror = (event) => {
        // EventSource errors don't provide much info - check readyState
        const readyState = eventSource.readyState;
        const stateMap = { 0: 'CONNECTING', 1: 'OPEN', 2: 'CLOSED' };
        console.warn(`[SSE] Connection error (state: ${stateMap[readyState] || readyState})`);

        // Only log if not already connected (avoid spam during normal reconnection)
        if (readyState === 2) {
          addLog('[SSE] Connection closed, backend may not be running', 'warning');
        }

        setSseConnected(false);
        // Try to reconnect after 2 seconds (only if not closed)
        if (eventSourceRef.current === eventSource && readyState !== 2) {
          setTimeout(() => {
            if (isRunning && !sseConnected) {
              addLog('[SSE] Attempting to reconnect...', 'warning');
              connectSSE();
            }
          }, 2000);
        }
      };

      eventSourceRef.current = eventSource;
      return sessionId;
    } catch (e) {
      console.error('Failed to create EventSource:', e);
      return null;
    }
  }, [addLog, isRunning, sseConnected]);

  // Disconnect SSE
  const disconnectSSE = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      sessionIdRef.current = null;
      setSseConnected(false);
      addLog('[SSE] Disconnected from backend log stream', 'info');
    }
  }, [addLog]);

  // Cleanup SSE on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  const runTests = async (quickTest = false) => {
    if (!organization) {
      Alert.alert('Error', 'Please log in first');
      return;
    }

    setIsRunning(true);
    setRunningTestType(quickTest ? 'quick' : 'full');
    setLogs([]);
    setResults(null);

    // Connect to SSE for real-time backend logs
    const sessionId = connectSSE();

    // Override console.log to capture test output
    const originalLog = console.log;
    const originalWarn = console.warn;
    const originalError = console.error;

    const formatArgs = (args) => args.map(arg =>
      typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
    ).join(' ');

    console.log = (...args) => {
      originalLog(...args);
      addLog(formatArgs(args), 'info');
    };
    console.warn = (...args) => {
      originalWarn(...args);
      addLog(formatArgs(args), 'warning');
    };
    console.error = (...args) => {
      originalError(...args);
      addLog(formatArgs(args), 'error');
    };

    try {
      let testResults;

      if (activeTab === TABS.SALES) {
        if (quickTest) {
          addLog('Starting quick sales smoke test...', 'info');
          testResults = await runQuickSmokeTest(db, { logSessionId: sessionId });
        } else {
          // Filter variations by selected categories
          const variations = TEST_VARIATIONS.filter(v => selectedSalesCategories[v.category]);
          addLog(`Starting full sales test with ${variations.length} variations...`, 'info');
          testResults = await runSalesDualWriteTests(db, {
            variations,
            cleanup: true,
            delayMs: 6000,
            logSessionId: sessionId,
          });
        }
      } else if (activeTab === TABS.SUPPLIES) {
        if (quickTest) {
          addLog('Starting quick supplies smoke test...', 'info');
          testResults = await runQuickSuppliesSmokeTest(db, { logSessionId: sessionId });
        } else {
          // Filter variations by selected categories
          const variations = SUPPLY_TEST_VARIATIONS.filter(v => selectedSuppliesCategories[v.category]);
          addLog(`Starting full supplies test with ${variations.length} variations...`, 'info');
          testResults = await runSuppliesDualWriteTests(db, {
            variations,
            cleanup: true,
            delayMs: 6000,
            logSessionId: sessionId,
          });
        }
      } else if (activeTab === TABS.CONSTANTS) {
        if (quickTest) {
          addLog('Starting quick constants smoke test...', 'info');
          testResults = await runQuickConstantsSmokeTest(db, { logSessionId: sessionId });
        } else {
          // Filter variations by selected categories
          const variations = CONSTANTS_TEST_VARIATIONS.filter(v => selectedConstantsCategories[v.category]);
          addLog(`Starting full constants test with ${variations.length} variations...`, 'info');
          testResults = await runConstantsDualWriteTests(db, {
            variations,
            cleanup: true,
            delayMs: 5000,
            logSessionId: sessionId,
          });
        }
      } else if (activeTab === TABS.ORGANIZATIONS) {
        if (quickTest) {
          addLog('Starting quick organization smoke test...', 'info');
          testResults = await runQuickOrganizationSmokeTest(db, { logSessionId: sessionId });
        } else {
          // Filter variations by selected categories
          const variations = ORGANIZATION_TEST_VARIATIONS.filter(v => selectedOrganizationCategories[v.category]);
          addLog(`Starting full organization test with ${variations.length} variations...`, 'info');
          testResults = await runOrganizationDualWriteTests(db, {
            variations,
            cleanup: true,
            delayMs: 5000,
            logSessionId: sessionId,
          });
        }
      } else if (activeTab === TABS.PRODUCTS) {
        if (quickTest) {
          addLog('Starting quick products smoke test...', 'info');
          testResults = await runQuickProductsSmokeTest(db, { logSessionId: sessionId });
        } else {
          // Filter variations by selected categories
          const variations = PRODUCT_TEST_VARIATIONS.filter(v => selectedProductsCategories[v.category]);
          addLog(`Starting full products test with ${variations.length} variations...`, 'info');
          testResults = await runProductsDualWriteTests(db, {
            variations,
            cleanup: true,
            delayMs: 5000,
            logSessionId: sessionId,
          });
        }
      } else if (activeTab === TABS.CATEGORIES) {
        if (quickTest) {
          addLog('Starting quick product categories smoke test...', 'info');
          testResults = await runQuickProductCategoriesSmokeTest(db, { logSessionId: sessionId });
        } else {
          // Filter variations by selected categories
          const variations = PRODUCT_CATEGORY_TEST_VARIATIONS.filter(v => selectedCategoriesCategories[v.category]);
          addLog(`Starting full product categories test with ${variations.length} variations...`, 'info');
          testResults = await runProductCategoriesDualWriteTests(db, {
            variations,
            cleanup: true,
            delayMs: 5000,
            logSessionId: sessionId,
          });
        }
      } else if (activeTab === TABS.SUPPLIERS) {
        if (quickTest) {
          addLog('Starting quick suppliers smoke test...', 'info');
          testResults = await runQuickSuppliersSmokeTest(db, { logSessionId: sessionId });
        } else {
          // Filter variations by selected categories
          const variations = SUPPLIER_TEST_VARIATIONS.filter(v => selectedSuppliersCategories[v.category]);
          addLog(`Starting full suppliers test with ${variations.length} variations...`, 'info');
          testResults = await runSuppliersDualWriteTests(db, {
            variations,
            cleanup: true,
            delayMs: 5000,
            logSessionId: sessionId,
          });
        }
      } else if (activeTab === TABS.STORES) {
        if (quickTest) {
          addLog('Starting quick stores smoke test...', 'info');
          testResults = await runQuickStoresSmokeTest(db, { logSessionId: sessionId });
        } else {
          // Filter variations by selected categories
          const variations = STORE_TEST_VARIATIONS.filter(v => selectedStoresCategories[v.category]);
          addLog(`Starting full stores test with ${variations.length} variations...`, 'info');
          testResults = await runStoresDualWriteTests(db, {
            variations,
            cleanup: true,
            delayMs: 5000,
            logSessionId: sessionId,
          });
        }
      } else if (activeTab === TABS.USERS) {
        if (quickTest) {
          addLog('Starting quick users smoke test...', 'info');
          testResults = await runQuickUsersSmokeTest(db, { logSessionId: sessionId });
        } else {
          // Filter variations by selected categories
          const variations = USER_TEST_VARIATIONS.filter(v => selectedUsersCategories[v.category]);
          addLog(`Starting full users test with ${variations.length} variations...`, 'info');
          testResults = await runUsersDualWriteTests(db, {
            variations,
            cleanup: true,
            delayMs: 5000,
            logSessionId: sessionId,
          });
        }
      } else if (activeTab === TABS.TRANSACTIONS) {
        // Transactions test - uses runFullTransactionTest which handles its own logic
        const selectedVariations = TRANSACTION_TEST_VARIATIONS
          .filter(v => selectedTransactionsCategories[v.category])
          .map(v => v.id);
        addLog(`Starting transactions test with ${selectedVariations.length} variations...`, 'info');
        const txResult = await runFullTransactionTest(
          (msg) => addLog(msg, 'info'),
          selectedVariations,
        );
        // Convert to standard format
        testResults = {
          passed: txResult.results.filter(r => r.passed).map(r => ({
            variation: TRANSACTION_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: txResult.results.filter(r => !r.passed).map(r => ({
            variation: TRANSACTION_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.RETURNS) {
        // Returns test - uses runFullReturnTest which handles its own logic
        const selectedVariations = RETURN_TEST_VARIATIONS
          .filter(v => selectedReturnsCategories[v.category])
          .map(v => v.id);
        addLog(`Starting returns test with ${selectedVariations.length} variations...`, 'info');
        const returnResult = await runFullReturnTest(
          (msg) => addLog(msg, 'info'),
          selectedVariations,
        );
        // Convert to standard format
        testResults = {
          passed: returnResult.results.filter(r => r.passed).map(r => ({
            variation: RETURN_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: returnResult.results.filter(r => !r.passed).map(r => ({
            variation: RETURN_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.TRANSFERS) {
        // Transfers test - uses runFullTransferTest which handles its own logic
        const selectedVariations = TRANSFER_TEST_VARIATIONS
          .filter(v => selectedTransfersCategories[v.category])
          .map(v => v.id);
        addLog(`Starting transfers test with ${selectedVariations.length} variations...`, 'info');
        const transferResult = await runFullTransferTest(
          (msg) => addLog(msg, 'info'),
          selectedVariations,
        );
        // Convert to standard format
        testResults = {
          passed: transferResult.results.filter(r => r.passed).map(r => ({
            variation: TRANSFER_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: transferResult.results.filter(r => !r.passed).map(r => ({
            variation: TRANSFER_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.REVISIONS) {
        // Revisions test - uses runFullRevisionTest which handles its own logic
        const selectedVariations = REVISION_TEST_VARIATIONS
          .filter(v => selectedRevisionsCategories[v.category])
          .map(v => v.id);
        addLog(`Starting revisions test with ${selectedVariations.length} variations...`, 'info');
        const revisionResult = await runFullRevisionTest(
          (msg) => addLog(msg, 'info'),
          selectedVariations,
        );
        // Convert to standard format
        testResults = {
          passed: revisionResult.results.filter(r => r.passed).map(r => ({
            variation: REVISION_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: revisionResult.results.filter(r => !r.passed).map(r => ({
            variation: REVISION_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.WRITEOFFS) {
        // Writeoffs test - uses runFullWriteoffTest which handles its own logic
        const selectedVariations = WRITEOFF_TEST_VARIATIONS
          .filter(v => selectedWriteoffsCategories[v.category])
          .map(v => v.id);
        addLog(`Starting writeoffs test with ${selectedVariations.length} variations...`, 'info');
        const writeoffResult = await runFullWriteoffTest(
          (msg) => addLog(msg, 'info'),
          selectedVariations,
        );
        // Convert to standard format
        testResults = {
          passed: writeoffResult.results.filter(r => r.passed).map(r => ({
            variation: WRITEOFF_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: writeoffResult.results.filter(r => !r.passed).map(r => ({
            variation: WRITEOFF_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.SPENDINGS) {
        // Spendings test - uses runFullSpendingTest which handles its own logic
        const selectedVariations = SPENDING_TEST_VARIATIONS
          .filter(v => selectedSpendingsCategories[v.category])
          .map(v => v.id);
        addLog(`Starting spendings test with ${selectedVariations.length} variations...`, 'info');
        const spendingResult = await runFullSpendingTest(
          (msg) => addLog(msg, 'info'),
          selectedVariations,
        );
        // Convert to standard format
        testResults = {
          passed: spendingResult.results.filter(r => r.passed).map(r => ({
            variation: SPENDING_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: spendingResult.results.filter(r => !r.passed).map(r => ({
            variation: SPENDING_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.CLIENTS) {
        // Clients test - uses runFullClientTest which handles its own logic
        const selectedVariations = CLIENT_TEST_VARIATIONS
          .filter(v => selectedClientsCategories[v.category])
          .map(v => v.id);
        addLog(`Starting clients test with ${selectedVariations.length} variations...`, 'info');
        const clientResult = await runFullClientTest(
          (msg) => addLog(msg, 'info'),
          selectedVariations,
        );
        // Convert to standard format
        testResults = {
          passed: clientResult.results.filter(r => r.passed).map(r => ({
            variation: CLIENT_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: clientResult.results.filter(r => !r.passed).map(r => ({
            variation: CLIENT_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.MANUFACTURES) {
        // Manufactures test - uses runFullManufactureTest which handles its own logic
        const selectedVariations = MANUFACTURE_TEST_VARIATIONS
          .filter(v => selectedManufacturesCategories[v.category])
          .map(v => v.id);
        addLog(`Starting manufactures test with ${selectedVariations.length} variations...`, 'info');
        const manufactureResult = await runFullManufactureTest(
          (msg) => addLog(msg, 'info'),
          selectedVariations,
        );
        // Convert to standard format
        testResults = {
          passed: manufactureResult.results.filter(r => r.passed).map(r => ({
            variation: MANUFACTURE_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: manufactureResult.results.filter(r => !r.passed).map(r => ({
            variation: MANUFACTURE_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.WORKING_HOURS) {
        // Working Hours test - uses runFullWorkingHoursTest which handles its own logic
        const selectedVariations = WORKING_HOURS_TEST_VARIATIONS
          .filter(v => selectedWorkingHoursCategories[v.category])
          .map(v => v.id);
        addLog(`Starting working hours test with ${selectedVariations.length} variations...`, 'info');
        const workingHoursResult = await runFullWorkingHoursTest(
          (msg) => addLog(msg, 'info'),
          selectedVariations,
        );
        // Convert to standard format
        testResults = {
          passed: workingHoursResult.results.filter(r => r.passed).map(r => ({
            variation: WORKING_HOURS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: workingHoursResult.results.filter(r => !r.passed).map(r => ({
            variation: WORKING_HOURS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.CASHBOX) {
        // Cashbox Transactions test - uses runFullCashboxTransactionsTest which handles its own logic
        const selectedVariations = CASHBOX_TRANSACTIONS_TEST_VARIATIONS
          .filter(v => selectedCashboxCategories[v.category])
          .map(v => v.id);
        addLog(`Starting cashbox transactions test with ${selectedVariations.length} variations...`, 'info');
        const cashboxResult = await runFullCashboxTransactionsTest(
          (msg) => addLog(msg, 'info'),
          selectedVariations,
        );
        // Convert to standard format
        testResults = {
          passed: cashboxResult.results.filter(r => r.passed).map(r => ({
            variation: CASHBOX_TRANSACTIONS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: cashboxResult.results.filter(r => !r.passed).map(r => ({
            variation: CASHBOX_TRANSACTIONS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.SAVED_CARTS) {
        // Saved Carts test - uses runFullSavedCartsTest which handles its own logic
        const selectedVariations = SAVED_CARTS_TEST_VARIATIONS
          .filter(v => selectedSavedCartsCategories[v.category])
          .map(v => v.id);
        addLog(`Starting saved carts test with ${selectedVariations.length} variations...`, 'info');
        const savedCartsResult = await runFullSavedCartsTest(
          (msg) => addLog(msg, 'info'),
          selectedVariations,
        );
        // Convert to standard format
        testResults = {
          passed: savedCartsResult.results.filter(r => r.passed).map(r => ({
            variation: SAVED_CARTS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: savedCartsResult.results.filter(r => !r.passed).map(r => ({
            variation: SAVED_CARTS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.SUPPLY_ORDERS) {
        // Supply Orders test - uses runFullSupplyOrdersTest which handles its own logic
        const selectedVariations = SUPPLY_ORDERS_TEST_VARIATIONS
          .filter(v => selectedSupplyOrdersCategories[v.category])
          .map(v => v.id);
        addLog(`Starting supply orders test with ${selectedVariations.length} variations...`, 'info');
        const supplyOrdersResult = await runFullSupplyOrdersTest(
          (msg) => addLog(msg, 'info'),
          selectedVariations,
        );
        // Convert to standard format
        testResults = {
          passed: supplyOrdersResult.results.filter(r => r.passed).map(r => ({
            variation: SUPPLY_ORDERS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: supplyOrdersResult.results.filter(r => !r.passed).map(r => ({
            variation: SUPPLY_ORDERS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.CHECKLISTS) {
        // Checklists test - uses runFullChecklistsTest which handles its own logic
        const selectedVariations = CHECKLISTS_TEST_VARIATIONS
          .filter(v => selectedChecklistsCategories[v.category])
          .map(v => v.id);
        addLog(`Starting checklists test with ${selectedVariations.length} variations...`, 'info');
        const checklistsResult = await runFullChecklistsTest(
          (msg) => addLog(msg, 'info'),
          selectedVariations,
        );
        // Convert to standard format
        testResults = {
          passed: checklistsResult.results.filter(r => r.passed).map(r => ({
            variation: CHECKLISTS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: checklistsResult.results.filter(r => !r.passed).map(r => ({
            variation: CHECKLISTS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.PRINT_TEMPLATES) {
        // Print Templates test - uses runFullPrintTemplatesTest which handles its own logic
        const selectedVariations = PRINT_TEMPLATES_TEST_VARIATIONS
          .filter(v => selectedPrintTemplatesCategories[v.category])
          .map(v => v.id);
        addLog(`Starting print templates test with ${selectedVariations.length} variations...`, 'info');
        const printTemplatesResult = await runFullPrintTemplatesTest(
          (msg) => addLog(msg, 'info'),
          selectedVariations,
        );
        // Convert to standard format
        testResults = {
          passed: printTemplatesResult.results.filter(r => r.passed).map(r => ({
            variation: PRINT_TEMPLATES_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: printTemplatesResult.results.filter(r => !r.passed).map(r => ({
            variation: PRINT_TEMPLATES_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.STATS) {
        // Stats tests - compare daily stats and product stats between Firestore and Supabase
        if (quickTest) {
          addLog('Starting quick stats smoke test...', 'info');
          testResults = await runQuickStatsSmokeTest(db, { logSessionId: sessionId });
        } else {
          addLog('Starting full stats comparison test...', 'info');
          testResults = await runStatsDualWriteTests(db, { logSessionId: sessionId });
        }
      }

      setResults(testResults);
    } catch (error) {
      addLog(`Test failed: ${error.message}`, 'error');
      Alert.alert('Error', error.message);
    } finally {
      // Restore console
      console.log = originalLog;
      console.warn = originalWarn;
      console.error = originalError;
      setIsRunning(false);
      setRunningTestType(null);
      setRunningTestId(null);
      // Disconnect SSE after a short delay to catch any final logs
      setTimeout(() => {
        disconnectSSE();
      }, 1000);
    }
  };

  // Run a specific test by ID
  const runSpecificTest = async (testId) => {
    if (!organization) {
      Alert.alert('Error', 'Please log in first');
      return;
    }

    // Find variation based on active tab
    const variations = activeTab === TABS.SALES ? TEST_VARIATIONS :
                       activeTab === TABS.SUPPLIES ? SUPPLY_TEST_VARIATIONS :
                       activeTab === TABS.PRODUCTS ? PRODUCT_TEST_VARIATIONS :
                       activeTab === TABS.CATEGORIES ? PRODUCT_CATEGORY_TEST_VARIATIONS :
                       activeTab === TABS.SUPPLIERS ? SUPPLIER_TEST_VARIATIONS :
                       activeTab === TABS.STORES ? STORE_TEST_VARIATIONS :
                       activeTab === TABS.USERS ? USER_TEST_VARIATIONS :
                       activeTab === TABS.TRANSACTIONS ? TRANSACTION_TEST_VARIATIONS :
                       activeTab === TABS.RETURNS ? RETURN_TEST_VARIATIONS :
                       activeTab === TABS.TRANSFERS ? TRANSFER_TEST_VARIATIONS :
                       activeTab === TABS.REVISIONS ? REVISION_TEST_VARIATIONS :
                       activeTab === TABS.WRITEOFFS ? WRITEOFF_TEST_VARIATIONS :
                       activeTab === TABS.SPENDINGS ? SPENDING_TEST_VARIATIONS :
                       activeTab === TABS.CLIENTS ? CLIENT_TEST_VARIATIONS :
                       activeTab === TABS.CONSTANTS ? CONSTANTS_TEST_VARIATIONS : ORGANIZATION_TEST_VARIATIONS;
    const variation = variations.find(v => v.id === testId);
    if (!variation) {
      Alert.alert('Error', `Test ${testId} not found`);
      return;
    }

    setIsRunning(true);
    setRunningTestType('specific');
    setRunningTestId(testId);
    setLogs([]);
    setResults(null);

    // Connect to SSE for real-time backend logs
    const sessionId = connectSSE();

    // Override console.log to capture test output
    const originalLog = console.log;
    const originalWarn = console.warn;
    const originalError = console.error;

    const formatArgs = (args) => args.map(arg =>
      typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
    ).join(' ');

    console.log = (...args) => {
      originalLog(...args);
      addLog(formatArgs(args), 'info');
    };
    console.warn = (...args) => {
      originalWarn(...args);
      addLog(formatArgs(args), 'warning');
    };
    console.error = (...args) => {
      originalError(...args);
      addLog(formatArgs(args), 'error');
    };

    try {
      addLog(`Running single test: ${testId} - ${variation.name}...`, 'info');

      let testResults;

      if (activeTab === TABS.TRANSACTIONS) {
        // Transactions use a different test runner
        const txResult = await runFullTransactionTest(
          (msg) => addLog(msg, 'info'),
          [testId],
        );
        // Convert to standard format
        testResults = {
          passed: txResult.results.filter(r => r.passed).map(r => ({
            variation: TRANSACTION_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: txResult.results.filter(r => !r.passed).map(r => ({
            variation: TRANSACTION_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.RETURNS) {
        // Returns use a different test runner
        const returnResult = await runFullReturnTest(
          (msg) => addLog(msg, 'info'),
          [testId],
        );
        // Convert to standard format
        testResults = {
          passed: returnResult.results.filter(r => r.passed).map(r => ({
            variation: RETURN_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: returnResult.results.filter(r => !r.passed).map(r => ({
            variation: RETURN_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.TRANSFERS) {
        // Transfers use a different test runner
        const transferResult = await runFullTransferTest(
          (msg) => addLog(msg, 'info'),
          [testId],
        );
        // Convert to standard format
        testResults = {
          passed: transferResult.results.filter(r => r.passed).map(r => ({
            variation: TRANSFER_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: transferResult.results.filter(r => !r.passed).map(r => ({
            variation: TRANSFER_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.REVISIONS) {
        // Revisions use a different test runner
        const revisionResult = await runFullRevisionTest(
          (msg) => addLog(msg, 'info'),
          [testId],
        );
        // Convert to standard format
        testResults = {
          passed: revisionResult.results.filter(r => r.passed).map(r => ({
            variation: REVISION_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: revisionResult.results.filter(r => !r.passed).map(r => ({
            variation: REVISION_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.WRITEOFFS) {
        // Writeoffs use a different test runner
        const writeoffResult = await runFullWriteoffTest(
          (msg) => addLog(msg, 'info'),
          [testId],
        );
        // Convert to standard format
        testResults = {
          passed: writeoffResult.results.filter(r => r.passed).map(r => ({
            variation: WRITEOFF_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: writeoffResult.results.filter(r => !r.passed).map(r => ({
            variation: WRITEOFF_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.SPENDINGS) {
        // Spendings use a different test runner
        const spendingResult = await runFullSpendingTest(
          (msg) => addLog(msg, 'info'),
          [testId],
        );
        // Convert to standard format
        testResults = {
          passed: spendingResult.results.filter(r => r.passed).map(r => ({
            variation: SPENDING_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: spendingResult.results.filter(r => !r.passed).map(r => ({
            variation: SPENDING_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.CLIENTS) {
        // Clients use a different test runner
        const clientResult = await runFullClientTest(
          (msg) => addLog(msg, 'info'),
          [testId],
        );
        // Convert to standard format
        testResults = {
          passed: clientResult.results.filter(r => r.passed).map(r => ({
            variation: CLIENT_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: clientResult.results.filter(r => !r.passed).map(r => ({
            variation: CLIENT_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.MANUFACTURES) {
        // Manufactures use a different test runner
        const manufactureResult = await runFullManufactureTest(
          (msg) => addLog(msg, 'info'),
          [testId],
        );
        // Convert to standard format
        testResults = {
          passed: manufactureResult.results.filter(r => r.passed).map(r => ({
            variation: MANUFACTURE_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: manufactureResult.results.filter(r => !r.passed).map(r => ({
            variation: MANUFACTURE_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.WORKING_HOURS) {
        // Working Hours use a different test runner
        const workingHoursResult = await runFullWorkingHoursTest(
          (msg) => addLog(msg, 'info'),
          [testId],
        );
        // Convert to standard format
        testResults = {
          passed: workingHoursResult.results.filter(r => r.passed).map(r => ({
            variation: WORKING_HOURS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: workingHoursResult.results.filter(r => !r.passed).map(r => ({
            variation: WORKING_HOURS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.CASHBOX) {
        // Cashbox Transactions use a different test runner
        const cashboxResult = await runFullCashboxTransactionsTest(
          (msg) => addLog(msg, 'info'),
          [testId],
        );
        // Convert to standard format
        testResults = {
          passed: cashboxResult.results.filter(r => r.passed).map(r => ({
            variation: CASHBOX_TRANSACTIONS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: cashboxResult.results.filter(r => !r.passed).map(r => ({
            variation: CASHBOX_TRANSACTIONS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.SAVED_CARTS) {
        // Saved Carts use a different test runner
        const savedCartsResult = await runFullSavedCartsTest(
          (msg) => addLog(msg, 'info'),
          [testId],
        );
        // Convert to standard format
        testResults = {
          passed: savedCartsResult.results.filter(r => r.passed).map(r => ({
            variation: SAVED_CARTS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: savedCartsResult.results.filter(r => !r.passed).map(r => ({
            variation: SAVED_CARTS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.SUPPLY_ORDERS) {
        // Supply Orders use a different test runner
        const supplyOrdersResult = await runFullSupplyOrdersTest(
          (msg) => addLog(msg, 'info'),
          [testId],
        );
        // Convert to standard format
        testResults = {
          passed: supplyOrdersResult.results.filter(r => r.passed).map(r => ({
            variation: SUPPLY_ORDERS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: supplyOrdersResult.results.filter(r => !r.passed).map(r => ({
            variation: SUPPLY_ORDERS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.CHECKLISTS) {
        // Checklists use a different test runner
        const checklistsResult = await runFullChecklistsTest(
          (msg) => addLog(msg, 'info'),
          [testId],
        );
        // Convert to standard format
        testResults = {
          passed: checklistsResult.results.filter(r => r.passed).map(r => ({
            variation: CHECKLISTS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: checklistsResult.results.filter(r => !r.passed).map(r => ({
            variation: CHECKLISTS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.PRINT_TEMPLATES) {
        // Print Templates use a different test runner
        const printTemplatesResult = await runFullPrintTemplatesTest(
          (msg) => addLog(msg, 'info'),
          [testId],
        );
        // Convert to standard format
        testResults = {
          passed: printTemplatesResult.results.filter(r => r.passed).map(r => ({
            variation: PRINT_TEMPLATES_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: printTemplatesResult.results.filter(r => !r.passed).map(r => ({
            variation: PRINT_TEMPLATES_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else {
        const runTestFn = activeTab === TABS.SALES ? runSalesDualWriteTests :
                          activeTab === TABS.SUPPLIES ? runSuppliesDualWriteTests :
                          activeTab === TABS.PRODUCTS ? runProductsDualWriteTests :
                          activeTab === TABS.CATEGORIES ? runProductCategoriesDualWriteTests :
                          activeTab === TABS.SUPPLIERS ? runSuppliersDualWriteTests :
                          activeTab === TABS.STORES ? runStoresDualWriteTests :
                          activeTab === TABS.USERS ? runUsersDualWriteTests :
                          activeTab === TABS.CONSTANTS ? runConstantsDualWriteTests : runOrganizationDualWriteTests;
        const delayMs = (activeTab === TABS.CONSTANTS || activeTab === TABS.ORGANIZATIONS || activeTab === TABS.PRODUCTS || activeTab === TABS.CATEGORIES || activeTab === TABS.SUPPLIERS || activeTab === TABS.STORES || activeTab === TABS.USERS) ? 5000 : 6000;
        testResults = await runTestFn(db, {
          variations: [variation],
          cleanup: true,
          delayMs,
          logSessionId: sessionId,
        });
      }

      setResults(testResults);
    } catch (error) {
      addLog(`Test failed: ${error.message}`, 'error');
      Alert.alert('Error', error.message);
    } finally {
      // Restore console
      console.log = originalLog;
      console.warn = originalWarn;
      console.error = originalError;
      setIsRunning(false);
      setRunningTestType(null);
      setRunningTestId(null);
      // Disconnect SSE after a short delay to catch any final logs
      setTimeout(() => {
        disconnectSSE();
      }, 1000);
    }
  };

  // Run all tests in a specific category
  const runCategoryTests = async (categoryId) => {
    if (!organization) {
      Alert.alert('Error', 'Please log in first');
      return;
    }

    const variations = activeTab === TABS.SALES ? TEST_VARIATIONS :
                       activeTab === TABS.SUPPLIES ? SUPPLY_TEST_VARIATIONS :
                       activeTab === TABS.PRODUCTS ? PRODUCT_TEST_VARIATIONS :
                       activeTab === TABS.CATEGORIES ? PRODUCT_CATEGORY_TEST_VARIATIONS :
                       activeTab === TABS.SUPPLIERS ? SUPPLIER_TEST_VARIATIONS :
                       activeTab === TABS.STORES ? STORE_TEST_VARIATIONS :
                       activeTab === TABS.USERS ? USER_TEST_VARIATIONS :
                       activeTab === TABS.TRANSACTIONS ? TRANSACTION_TEST_VARIATIONS :
                       activeTab === TABS.RETURNS ? RETURN_TEST_VARIATIONS :
                       activeTab === TABS.TRANSFERS ? TRANSFER_TEST_VARIATIONS :
                       activeTab === TABS.REVISIONS ? REVISION_TEST_VARIATIONS :
                       activeTab === TABS.WRITEOFFS ? WRITEOFF_TEST_VARIATIONS :
                       activeTab === TABS.SPENDINGS ? SPENDING_TEST_VARIATIONS :
                       activeTab === TABS.CLIENTS ? CLIENT_TEST_VARIATIONS :
                       activeTab === TABS.CONSTANTS ? CONSTANTS_TEST_VARIATIONS : ORGANIZATION_TEST_VARIATIONS;
    const categoryTests = variations.filter(v => v.category === categoryId);
    if (categoryTests.length === 0) {
      Alert.alert('Error', `No tests found for category ${categoryId}`);
      return;
    }

    setIsRunning(true);
    setRunningTestType('category');
    setRunningCategory(categoryId);
    setLogs([]);
    setResults(null);

    // Connect to SSE for real-time backend logs
    const sessionId = connectSSE();

    // Override console.log to capture test output
    const originalLog = console.log;
    const originalWarn = console.warn;
    const originalError = console.error;

    const formatArgs = (args) => args.map(arg =>
      typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
    ).join(' ');

    console.log = (...args) => {
      originalLog(...args);
      addLog(formatArgs(args), 'info');
    };
    console.warn = (...args) => {
      originalWarn(...args);
      addLog(formatArgs(args), 'warning');
    };
    console.error = (...args) => {
      originalError(...args);
      addLog(formatArgs(args), 'error');
    };

    try {
      addLog(`Running ${categoryTests.length} tests from category: ${categoryId}...`, 'info');

      let testResults;

      if (activeTab === TABS.TRANSACTIONS) {
        // Transactions use a different test runner
        const testIds = categoryTests.map(t => t.id);
        const txResult = await runFullTransactionTest(
          (msg) => addLog(msg, 'info'),
          testIds,
        );
        // Convert to standard format
        testResults = {
          passed: txResult.results.filter(r => r.passed).map(r => ({
            variation: TRANSACTION_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: txResult.results.filter(r => !r.passed).map(r => ({
            variation: TRANSACTION_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.RETURNS) {
        // Returns use a different test runner
        const testIds = categoryTests.map(t => t.id);
        const returnResult = await runFullReturnTest(
          (msg) => addLog(msg, 'info'),
          testIds,
        );
        // Convert to standard format
        testResults = {
          passed: returnResult.results.filter(r => r.passed).map(r => ({
            variation: RETURN_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: returnResult.results.filter(r => !r.passed).map(r => ({
            variation: RETURN_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.TRANSFERS) {
        // Transfers use a different test runner
        const testIds = categoryTests.map(t => t.id);
        const transferResult = await runFullTransferTest(
          (msg) => addLog(msg, 'info'),
          testIds,
        );
        // Convert to standard format
        testResults = {
          passed: transferResult.results.filter(r => r.passed).map(r => ({
            variation: TRANSFER_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: transferResult.results.filter(r => !r.passed).map(r => ({
            variation: TRANSFER_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.REVISIONS) {
        // Revisions use a different test runner
        const testIds = categoryTests.map(t => t.id);
        const revisionResult = await runFullRevisionTest(
          (msg) => addLog(msg, 'info'),
          testIds,
        );
        // Convert to standard format
        testResults = {
          passed: revisionResult.results.filter(r => r.passed).map(r => ({
            variation: REVISION_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: revisionResult.results.filter(r => !r.passed).map(r => ({
            variation: REVISION_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.WRITEOFFS) {
        // Writeoffs use a different test runner
        const testIds = categoryTests.map(t => t.id);
        const writeoffResult = await runFullWriteoffTest(
          (msg) => addLog(msg, 'info'),
          testIds,
        );
        // Convert to standard format
        testResults = {
          passed: writeoffResult.results.filter(r => r.passed).map(r => ({
            variation: WRITEOFF_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: writeoffResult.results.filter(r => !r.passed).map(r => ({
            variation: WRITEOFF_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.SPENDINGS) {
        // Spendings use a different test runner
        const testIds = categoryTests.map(t => t.id);
        const spendingResult = await runFullSpendingTest(
          (msg) => addLog(msg, 'info'),
          testIds,
        );
        // Convert to standard format
        testResults = {
          passed: spendingResult.results.filter(r => r.passed).map(r => ({
            variation: SPENDING_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: spendingResult.results.filter(r => !r.passed).map(r => ({
            variation: SPENDING_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.CLIENTS) {
        // Clients use a different test runner
        const testIds = categoryTests.map(t => t.id);
        const clientResult = await runFullClientTest(
          (msg) => addLog(msg, 'info'),
          testIds,
        );
        // Convert to standard format
        testResults = {
          passed: clientResult.results.filter(r => r.passed).map(r => ({
            variation: CLIENT_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: clientResult.results.filter(r => !r.passed).map(r => ({
            variation: CLIENT_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.MANUFACTURES) {
        // Manufactures use a different test runner
        const testIds = categoryTests.map(t => t.id);
        const manufactureResult = await runFullManufactureTest(
          (msg) => addLog(msg, 'info'),
          testIds,
        );
        // Convert to standard format
        testResults = {
          passed: manufactureResult.results.filter(r => r.passed).map(r => ({
            variation: MANUFACTURE_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: manufactureResult.results.filter(r => !r.passed).map(r => ({
            variation: MANUFACTURE_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.WORKING_HOURS) {
        // Working Hours use a different test runner
        const testIds = categoryTests.map(t => t.id);
        const workingHoursResult = await runFullWorkingHoursTest(
          (msg) => addLog(msg, 'info'),
          testIds,
        );
        // Convert to standard format
        testResults = {
          passed: workingHoursResult.results.filter(r => r.passed).map(r => ({
            variation: WORKING_HOURS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: workingHoursResult.results.filter(r => !r.passed).map(r => ({
            variation: WORKING_HOURS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.CASHBOX) {
        // Cashbox Transactions use a different test runner
        const testIds = categoryTests.map(t => t.id);
        const cashboxResult = await runFullCashboxTransactionsTest(
          (msg) => addLog(msg, 'info'),
          testIds,
        );
        // Convert to standard format
        testResults = {
          passed: cashboxResult.results.filter(r => r.passed).map(r => ({
            variation: CASHBOX_TRANSACTIONS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: cashboxResult.results.filter(r => !r.passed).map(r => ({
            variation: CASHBOX_TRANSACTIONS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.SAVED_CARTS) {
        // Saved Carts use a different test runner
        const testIds = categoryTests.map(t => t.id);
        const savedCartsResult = await runFullSavedCartsTest(
          (msg) => addLog(msg, 'info'),
          testIds,
        );
        // Convert to standard format
        testResults = {
          passed: savedCartsResult.results.filter(r => r.passed).map(r => ({
            variation: SAVED_CARTS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: savedCartsResult.results.filter(r => !r.passed).map(r => ({
            variation: SAVED_CARTS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.SUPPLY_ORDERS) {
        // Supply Orders use a different test runner
        const testIds = categoryTests.map(t => t.id);
        const supplyOrdersResult = await runFullSupplyOrdersTest(
          (msg) => addLog(msg, 'info'),
          testIds,
        );
        // Convert to standard format
        testResults = {
          passed: supplyOrdersResult.results.filter(r => r.passed).map(r => ({
            variation: SUPPLY_ORDERS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: supplyOrdersResult.results.filter(r => !r.passed).map(r => ({
            variation: SUPPLY_ORDERS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.CHECKLISTS) {
        // Checklists use a different test runner
        const testIds = categoryTests.map(t => t.id);
        const checklistsResult = await runFullChecklistsTest(
          (msg) => addLog(msg, 'info'),
          testIds,
        );
        // Convert to standard format
        testResults = {
          passed: checklistsResult.results.filter(r => r.passed).map(r => ({
            variation: CHECKLISTS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: checklistsResult.results.filter(r => !r.passed).map(r => ({
            variation: CHECKLISTS_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else if (activeTab === TABS.PRINT_TEMPLATES) {
        // Print Templates use a different test runner
        const testIds = categoryTests.map(t => t.id);
        const printTemplatesResult = await runFullPrintTemplatesTest(
          (msg) => addLog(msg, 'info'),
          testIds,
        );
        // Convert to standard format
        testResults = {
          passed: printTemplatesResult.results.filter(r => r.passed).map(r => ({
            variation: PRINT_TEMPLATES_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
          })),
          failed: printTemplatesResult.results.filter(r => !r.passed).map(r => ({
            variation: PRINT_TEMPLATES_TEST_VARIATIONS.find(v => v.id === r.variationId),
            result: r.comparisonResult,
            error: r.error,
          })),
          skipped: [],
        };
      } else {
        const runTestFn = activeTab === TABS.SALES ? runSalesDualWriteTests :
                          activeTab === TABS.SUPPLIES ? runSuppliesDualWriteTests :
                          activeTab === TABS.PRODUCTS ? runProductsDualWriteTests :
                          activeTab === TABS.CATEGORIES ? runProductCategoriesDualWriteTests :
                          activeTab === TABS.SUPPLIERS ? runSuppliersDualWriteTests :
                          activeTab === TABS.STORES ? runStoresDualWriteTests :
                          activeTab === TABS.USERS ? runUsersDualWriteTests :
                          activeTab === TABS.CONSTANTS ? runConstantsDualWriteTests : runOrganizationDualWriteTests;
        const delayMs = (activeTab === TABS.CONSTANTS || activeTab === TABS.ORGANIZATIONS || activeTab === TABS.PRODUCTS || activeTab === TABS.CATEGORIES || activeTab === TABS.SUPPLIERS || activeTab === TABS.STORES || activeTab === TABS.USERS) ? 5000 : 6000;
        testResults = await runTestFn(db, {
          variations: categoryTests,
          cleanup: true,
          delayMs,
          logSessionId: sessionId,
        });
      }

      setResults(testResults);
    } catch (error) {
      addLog(`Test failed: ${error.message}`, 'error');
      Alert.alert('Error', error.message);
    } finally {
      // Restore console
      console.log = originalLog;
      console.warn = originalWarn;
      console.error = originalError;
      setIsRunning(false);
      setRunningTestType(null);
      setRunningCategory(null);
      // Disconnect SSE after a short delay to catch any final logs
      setTimeout(() => {
        disconnectSSE();
      }, 1000);
    }
  };

  const toggleSalesCategory = (category) => {
    setSelectedSalesCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleSuppliesCategory = (category) => {
    setSelectedSuppliesCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleConstantsCategory = (category) => {
    setSelectedConstantsCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleOrganizationCategory = (category) => {
    setSelectedOrganizationCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleProductsCategory = (category) => {
    setSelectedProductsCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleCategoriesCategory = (category) => {
    setSelectedCategoriesCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleSuppliersCategory = (category) => {
    setSelectedSuppliersCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleStoresCategory = (category) => {
    setSelectedStoresCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleUsersCategory = (category) => {
    setSelectedUsersCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleTransactionsCategory = (category) => {
    setSelectedTransactionsCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleReturnsCategory = (category) => {
    setSelectedReturnsCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleTransfersCategory = (category) => {
    setSelectedTransfersCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleRevisionsCategory = (category) => {
    setSelectedRevisionsCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleWriteoffsCategory = (category) => {
    setSelectedWriteoffsCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleSpendingsCategory = (category) => {
    setSelectedSpendingsCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleClientsCategory = (category) => {
    setSelectedClientsCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleManufacturesCategory = (category) => {
    setSelectedManufacturesCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleWorkingHoursCategory = (category) => {
    setSelectedWorkingHoursCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleCashboxCategory = (category) => {
    setSelectedCashboxCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleSavedCartsCategory = (category) => {
    setSelectedSavedCartsCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleSupplyOrdersCategory = (category) => {
    setSelectedSupplyOrdersCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleChecklistsCategory = (category) => {
    setSelectedChecklistsCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const togglePrintTemplatesCategory = (category) => {
    setSelectedPrintTemplatesCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleStatsCategory = (category) => {
    setSelectedStatsCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleExpanded = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const copyLogs = async () => {
    const logText = logs.map(log => `[${log.timestamp}] ${log.message}`).join('\n');
    await Clipboard.setStringAsync(logText);
    Alert.alert('Copied', 'Logs copied to clipboard');
  };

  // Sales categories
  const salesCategories = [
    { id: 'client', label: 'Client Variations', count: TEST_VARIATIONS.filter(v => v.category === 'client').length },
    { id: 'payment', label: 'Payment Methods', count: TEST_VARIATIONS.filter(v => v.category === 'payment').length },
    { id: 'cart', label: 'Cart Items', count: TEST_VARIATIONS.filter(v => v.category === 'cart').length },
    { id: 'discount', label: 'Discounts', count: TEST_VARIATIONS.filter(v => v.category === 'discount').length },
    { id: 'purchaseMethod', label: 'Purchase Methods', count: TEST_VARIATIONS.filter(v => v.category === 'purchaseMethod').length },
    { id: 'delivery', label: 'Delivery', count: TEST_VARIATIONS.filter(v => v.category === 'delivery').length },
    { id: 'optional', label: 'Optional Fields', count: TEST_VARIATIONS.filter(v => v.category === 'optional').length },
    { id: 'date', label: 'Date/Time Variations', count: TEST_VARIATIONS.filter(v => v.category === 'date').length },
    { id: 'lifecycle', label: 'Lifecycle (Updates/Returns/Delete)', count: TEST_VARIATIONS.filter(v => v.category === 'lifecycle').length },
    { id: 'cascading', label: '⭐ Cascading Writes (P0)', count: TEST_VARIATIONS.filter(v => v.category === 'cascading').length, priority: 'P0' },
  ];

  // Supplies categories
  const suppliesCategories = [
    { id: 'items', label: 'Items Variations', count: SUPPLY_TEST_VARIATIONS.filter(v => v.category === 'items').length },
    { id: 'payment', label: 'Payment Status', count: SUPPLY_TEST_VARIATIONS.filter(v => v.category === 'payment').length },
    { id: 'supplier', label: 'Supplier', count: SUPPLY_TEST_VARIATIONS.filter(v => v.category === 'supplier').length },
    { id: 'optional', label: 'Optional Fields', count: SUPPLY_TEST_VARIATIONS.filter(v => v.category === 'optional').length },
    { id: 'date', label: 'Date/Time Variations', count: SUPPLY_TEST_VARIATIONS.filter(v => v.category === 'date').length },
    { id: 'lifecycle', label: 'Lifecycle (Paid/Unpaid/Delete)', count: SUPPLY_TEST_VARIATIONS.filter(v => v.category === 'lifecycle').length },
    { id: 'cascading', label: '⭐ Cascading Writes (P0)', count: SUPPLY_TEST_VARIATIONS.filter(v => v.category === 'cascading').length, priority: 'P0' },
  ];

  // Constants categories
  const constantsCategories = [
    { id: 'wallets', label: 'Wallets', count: CONSTANTS_TEST_VARIATIONS.filter(v => v.category === 'wallets').length },
    { id: 'payment_methods', label: 'Payment Methods', count: CONSTANTS_TEST_VARIATIONS.filter(v => v.category === 'payment_methods').length },
    { id: 'roles', label: 'Roles', count: CONSTANTS_TEST_VARIATIONS.filter(v => v.category === 'roles').length },
    { id: 'product_units', label: 'Product Units', count: CONSTANTS_TEST_VARIATIONS.filter(v => v.category === 'product_units').length },
    { id: 'spending_categories', label: 'Spending Categories', count: CONSTANTS_TEST_VARIATIONS.filter(v => v.category === 'spending_categories').length },
    { id: 'discount_options', label: 'Discount Options', count: CONSTANTS_TEST_VARIATIONS.filter(v => v.category === 'discount_options').length },
    { id: 'write_off_reasons', label: 'Write-off Reasons', count: CONSTANTS_TEST_VARIATIONS.filter(v => v.category === 'write_off_reasons').length },
    { id: 'return_reasons', label: 'Return Reasons', count: CONSTANTS_TEST_VARIATIONS.filter(v => v.category === 'return_reasons').length },
    { id: 'purchase_methods', label: 'Purchase Methods', count: CONSTANTS_TEST_VARIATIONS.filter(v => v.category === 'purchase_methods').length },
  ];

  // Organization categories
  const organizationCategories = [
    { id: 'modules', label: 'Module Settings', count: ORGANIZATION_TEST_VARIATIONS.filter(v => v.category === 'modules').length },
    { id: 'marketplace', label: 'Marketplace', count: ORGANIZATION_TEST_VARIATIONS.filter(v => v.category === 'marketplace').length },
    { id: 'link_settings', label: 'Link Settings', count: ORGANIZATION_TEST_VARIATIONS.filter(v => v.category === 'link_settings').length },
    { id: 'stock_settings', label: 'Stock Settings', count: ORGANIZATION_TEST_VARIATIONS.filter(v => v.category === 'stock_settings').length },
    { id: 'basic_info', label: 'Basic Info', count: ORGANIZATION_TEST_VARIATIONS.filter(v => v.category === 'basic_info').length },
    { id: 'currency', label: 'Currency', count: ORGANIZATION_TEST_VARIATIONS.filter(v => v.category === 'currency').length },
  ];

  // Products categories
  const productsCategories = [
    { id: 'basic', label: 'Basic Fields', count: PRODUCT_TEST_VARIATIONS.filter(v => v.category === 'basic').length },
    { id: 'relations', label: 'Category & Supplier', count: PRODUCT_TEST_VARIATIONS.filter(v => v.category === 'relations').length },
    { id: 'pricing', label: 'Store Prices', count: PRODUCT_TEST_VARIATIONS.filter(v => v.category === 'pricing').length },
    { id: 'types', label: 'Product Types', count: PRODUCT_TEST_VARIATIONS.filter(v => v.category === 'types').length },
    { id: 'inventory', label: 'Stock Management', count: PRODUCT_TEST_VARIATIONS.filter(v => v.category === 'inventory').length },
    { id: 'lifecycle', label: 'Lifecycle (Update/Delete)', count: PRODUCT_TEST_VARIATIONS.filter(v => v.category === 'lifecycle').length },
  ];

  // Product Categories categories
  const categoriesCategories = [
    { id: 'basic', label: 'Basic Fields', count: PRODUCT_CATEGORY_TEST_VARIATIONS.filter(v => v.category === 'basic').length },
    { id: 'lifecycle', label: 'Lifecycle (Update/Delete)', count: PRODUCT_CATEGORY_TEST_VARIATIONS.filter(v => v.category === 'lifecycle').length },
  ];

  // Suppliers categories
  const suppliersCategories = [
    { id: 'basic', label: 'Basic Fields', count: SUPPLIER_TEST_VARIATIONS.filter(v => v.category === 'basic').length },
    { id: 'lifecycle', label: 'Lifecycle (Update/Delete)', count: SUPPLIER_TEST_VARIATIONS.filter(v => v.category === 'lifecycle').length },
  ];

  // Stores categories
  const storesCategories = [
    { id: 'basic', label: 'Basic Fields', count: STORE_TEST_VARIATIONS.filter(v => v.category === 'basic').length },
    { id: 'lifecycle', label: 'Lifecycle (Update/Delete)', count: STORE_TEST_VARIATIONS.filter(v => v.category === 'lifecycle').length },
  ];

  // Users categories
  const usersCategories = [
    { id: 'basic', label: 'Basic Fields', count: USER_TEST_VARIATIONS.filter(v => v.category === 'basic').length },
    { id: 'store', label: 'Store Assignment', count: USER_TEST_VARIATIONS.filter(v => v.category === 'store').length },
  ];

  // Transactions categories
  const transactionsCategories = [
    { id: 'create', label: 'Create Transactions', count: TRANSACTION_TEST_VARIATIONS.filter(v => v.category === 'create').length },
    { id: 'delete', label: 'Delete Transaction', count: TRANSACTION_TEST_VARIATIONS.filter(v => v.category === 'delete').length },
  ];

  // Returns categories
  const returnsCategories = [
    { id: 'create', label: 'Create Returns', count: RETURN_TEST_VARIATIONS.filter(v => v.category === 'create').length },
    { id: 'delete', label: 'Delete Return', count: RETURN_TEST_VARIATIONS.filter(v => v.category === 'delete').length },
  ];

  // Transfers categories
  const transfersCategories = [
    { id: 'create', label: 'Create Transfers', count: TRANSFER_TEST_VARIATIONS.filter(v => v.category === 'create').length },
    { id: 'delete', label: 'Delete Transfer', count: TRANSFER_TEST_VARIATIONS.filter(v => v.category === 'delete').length },
  ];

  // Revisions categories
  const revisionsCategories = [
    { id: 'create', label: 'Create Revisions', count: REVISION_TEST_VARIATIONS.filter(v => v.category === 'create').length },
    { id: 'delete', label: 'Delete Revision', count: REVISION_TEST_VARIATIONS.filter(v => v.category === 'delete').length },
  ];

  const writeoffsCategories = [
    { id: 'create', label: 'Create Writeoffs', count: WRITEOFF_TEST_VARIATIONS.filter(v => v.category === 'create').length },
    { id: 'delete', label: 'Delete Writeoff', count: WRITEOFF_TEST_VARIATIONS.filter(v => v.category === 'delete').length },
  ];

  const spendingsCategories = [
    { id: 'create', label: 'Create Spendings', count: SPENDING_TEST_VARIATIONS.filter(v => v.category === 'create').length },
    { id: 'delete', label: 'Delete Spending', count: SPENDING_TEST_VARIATIONS.filter(v => v.category === 'delete').length },
  ];

  const clientsCategories = [
    { id: 'create', label: 'Create Client', count: CLIENT_TEST_VARIATIONS.filter(v => v.category === 'create').length },
    { id: 'update', label: 'Update Client', count: CLIENT_TEST_VARIATIONS.filter(v => v.category === 'update').length },
    { id: 'delete', label: 'Delete Client', count: CLIENT_TEST_VARIATIONS.filter(v => v.category === 'delete').length },
  ];

  const manufacturesCategories = [
    { id: 'create', label: 'Create Manufacture', count: MANUFACTURE_TEST_VARIATIONS.filter(v => v.category === 'create').length },
    { id: 'delete', label: 'Delete Manufacture', count: MANUFACTURE_TEST_VARIATIONS.filter(v => v.category === 'delete').length },
  ];

  const workingHoursCategories = [
    { id: 'create', label: 'Create Working Hours', count: WORKING_HOURS_TEST_VARIATIONS.filter(v => v.category === 'create').length },
    { id: 'update', label: 'Update Working Hours', count: WORKING_HOURS_TEST_VARIATIONS.filter(v => v.category === 'update').length },
    { id: 'delete', label: 'Delete Working Hours', count: WORKING_HOURS_TEST_VARIATIONS.filter(v => v.category === 'delete').length },
  ];

  // Cashbox Transactions categories
  const cashboxCategories = [
    { id: 'create', label: 'Create Cashbox Transaction', count: CASHBOX_TRANSACTIONS_TEST_VARIATIONS.filter(v => v.category === 'create').length },
    { id: 'update', label: 'Update Cashbox Transaction', count: CASHBOX_TRANSACTIONS_TEST_VARIATIONS.filter(v => v.category === 'update').length },
    { id: 'delete', label: 'Delete Cashbox Transaction', count: CASHBOX_TRANSACTIONS_TEST_VARIATIONS.filter(v => v.category === 'delete').length },
  ];

  // Saved Carts categories
  const savedCartsCategories = [
    { id: 'create', label: 'Create Saved Cart', count: SAVED_CARTS_TEST_VARIATIONS.filter(v => v.category === 'create').length },
    { id: 'update', label: 'Update Saved Cart', count: SAVED_CARTS_TEST_VARIATIONS.filter(v => v.category === 'update').length },
    { id: 'delete', label: 'Delete Saved Cart', count: SAVED_CARTS_TEST_VARIATIONS.filter(v => v.category === 'delete').length },
  ];

  // Supply Orders categories
  const supplyOrdersCategories = [
    { id: 'create', label: 'Create Supply Order', count: SUPPLY_ORDERS_TEST_VARIATIONS.filter(v => v.category === 'create').length },
    { id: 'update', label: 'Update Supply Order', count: SUPPLY_ORDERS_TEST_VARIATIONS.filter(v => v.category === 'update').length },
    { id: 'delete', label: 'Delete Supply Order', count: SUPPLY_ORDERS_TEST_VARIATIONS.filter(v => v.category === 'delete').length },
  ];

  // Checklists categories
  const checklistsCategories = [
    { id: 'create', label: 'Create Checklist', count: CHECKLISTS_TEST_VARIATIONS.filter(v => v.category === 'create').length },
    { id: 'update', label: 'Update Checklist', count: CHECKLISTS_TEST_VARIATIONS.filter(v => v.category === 'update').length },
  ];

  // Print Templates categories
  const printTemplatesCategories = [
    { id: 'create', label: 'Create Template', count: PRINT_TEMPLATES_TEST_VARIATIONS.filter(v => v.category === 'create').length },
    { id: 'update', label: 'Update Template', count: PRINT_TEMPLATES_TEST_VARIATIONS.filter(v => v.category === 'update').length },
    { id: 'delete', label: 'Delete Template', count: PRINT_TEMPLATES_TEST_VARIATIONS.filter(v => v.category === 'delete').length },
  ];

  const statsCategories = [
    { id: 'daily_stats', label: 'Daily Stats', count: STATS_TEST_VARIATIONS.filter(v => v.category === 'daily_stats').length },
    { id: 'product_stats', label: 'Product Stats', count: STATS_TEST_VARIATIONS.filter(v => v.category === 'product_stats').length },
  ];

  const selectedSalesCount = TEST_VARIATIONS.filter(v => selectedSalesCategories[v.category]).length;
  const selectedSuppliesCount = SUPPLY_TEST_VARIATIONS.filter(v => selectedSuppliesCategories[v.category]).length;
  const selectedConstantsCount = CONSTANTS_TEST_VARIATIONS.filter(v => selectedConstantsCategories[v.category]).length;
  const selectedOrganizationCount = ORGANIZATION_TEST_VARIATIONS.filter(v => selectedOrganizationCategories[v.category]).length;
  const selectedProductsCount = PRODUCT_TEST_VARIATIONS.filter(v => selectedProductsCategories[v.category]).length;
  const selectedCategoriesCount = PRODUCT_CATEGORY_TEST_VARIATIONS.filter(v => selectedCategoriesCategories[v.category]).length;
  const selectedSuppliersCount = SUPPLIER_TEST_VARIATIONS.filter(v => selectedSuppliersCategories[v.category]).length;
  const selectedStoresCount = STORE_TEST_VARIATIONS.filter(v => selectedStoresCategories[v.category]).length;
  const selectedUsersCount = USER_TEST_VARIATIONS.filter(v => selectedUsersCategories[v.category]).length;
  const selectedTransactionsCount = TRANSACTION_TEST_VARIATIONS.filter(v => selectedTransactionsCategories[v.category]).length;
  const selectedReturnsCount = RETURN_TEST_VARIATIONS.filter(v => selectedReturnsCategories[v.category]).length;
  const selectedTransfersCount = TRANSFER_TEST_VARIATIONS.filter(v => selectedTransfersCategories[v.category]).length;
  const selectedRevisionsCount = REVISION_TEST_VARIATIONS.filter(v => selectedRevisionsCategories[v.category]).length;
  const selectedWriteoffsCount = WRITEOFF_TEST_VARIATIONS.filter(v => selectedWriteoffsCategories[v.category]).length;
  const selectedSpendingsCount = SPENDING_TEST_VARIATIONS.filter(v => selectedSpendingsCategories[v.category]).length;
  const selectedClientsCount = CLIENT_TEST_VARIATIONS.filter(v => selectedClientsCategories[v.category]).length;
  const selectedManufacturesCount = MANUFACTURE_TEST_VARIATIONS.filter(v => selectedManufacturesCategories[v.category]).length;
  const selectedWorkingHoursCount = WORKING_HOURS_TEST_VARIATIONS.filter(v => selectedWorkingHoursCategories[v.category]).length;
  const selectedCashboxCount = CASHBOX_TRANSACTIONS_TEST_VARIATIONS.filter(v => selectedCashboxCategories[v.category]).length;
  const selectedSavedCartsCount = SAVED_CARTS_TEST_VARIATIONS.filter(v => selectedSavedCartsCategories[v.category]).length;
  const selectedSupplyOrdersCount = SUPPLY_ORDERS_TEST_VARIATIONS.filter(v => selectedSupplyOrdersCategories[v.category]).length;
  const selectedChecklistsCount = CHECKLISTS_TEST_VARIATIONS.filter(v => selectedChecklistsCategories[v.category]).length;
  const selectedPrintTemplatesCount = PRINT_TEMPLATES_TEST_VARIATIONS.filter(v => selectedPrintTemplatesCategories[v.category]).length;
  const selectedStatsCount = STATS_TEST_VARIATIONS.filter(v => selectedStatsCategories[v.category]).length;

  const currentCategories = activeTab === TABS.SALES ? salesCategories :
                            activeTab === TABS.SUPPLIES ? suppliesCategories :
                            activeTab === TABS.PRODUCTS ? productsCategories :
                            activeTab === TABS.CATEGORIES ? categoriesCategories :
                            activeTab === TABS.SUPPLIERS ? suppliersCategories :
                            activeTab === TABS.STORES ? storesCategories :
                            activeTab === TABS.USERS ? usersCategories :
                            activeTab === TABS.TRANSACTIONS ? transactionsCategories :
                            activeTab === TABS.RETURNS ? returnsCategories :
                            activeTab === TABS.TRANSFERS ? transfersCategories :
                            activeTab === TABS.REVISIONS ? revisionsCategories :
                            activeTab === TABS.WRITEOFFS ? writeoffsCategories :
                            activeTab === TABS.SPENDINGS ? spendingsCategories :
                            activeTab === TABS.CLIENTS ? clientsCategories :
                            activeTab === TABS.MANUFACTURES ? manufacturesCategories :
                            activeTab === TABS.WORKING_HOURS ? workingHoursCategories :
                            activeTab === TABS.CASHBOX ? cashboxCategories :
                            activeTab === TABS.SAVED_CARTS ? savedCartsCategories :
                            activeTab === TABS.SUPPLY_ORDERS ? supplyOrdersCategories :
                            activeTab === TABS.CHECKLISTS ? checklistsCategories :
                            activeTab === TABS.PRINT_TEMPLATES ? printTemplatesCategories :
                            activeTab === TABS.STATS ? statsCategories :
                            activeTab === TABS.CONSTANTS ? constantsCategories : organizationCategories;
  const currentSelectedCategories = activeTab === TABS.SALES ? selectedSalesCategories :
                                    activeTab === TABS.SUPPLIES ? selectedSuppliesCategories :
                                    activeTab === TABS.PRODUCTS ? selectedProductsCategories :
                                    activeTab === TABS.CATEGORIES ? selectedCategoriesCategories :
                                    activeTab === TABS.SUPPLIERS ? selectedSuppliersCategories :
                                    activeTab === TABS.STORES ? selectedStoresCategories :
                                    activeTab === TABS.USERS ? selectedUsersCategories :
                                    activeTab === TABS.TRANSACTIONS ? selectedTransactionsCategories :
                                    activeTab === TABS.RETURNS ? selectedReturnsCategories :
                                    activeTab === TABS.TRANSFERS ? selectedTransfersCategories :
                                    activeTab === TABS.REVISIONS ? selectedRevisionsCategories :
                                    activeTab === TABS.WRITEOFFS ? selectedWriteoffsCategories :
                                    activeTab === TABS.SPENDINGS ? selectedSpendingsCategories :
                                    activeTab === TABS.CLIENTS ? selectedClientsCategories :
                                    activeTab === TABS.MANUFACTURES ? selectedManufacturesCategories :
                                    activeTab === TABS.WORKING_HOURS ? selectedWorkingHoursCategories :
                                    activeTab === TABS.CASHBOX ? selectedCashboxCategories :
                                    activeTab === TABS.SAVED_CARTS ? selectedSavedCartsCategories :
                                    activeTab === TABS.SUPPLY_ORDERS ? selectedSupplyOrdersCategories :
                                    activeTab === TABS.CHECKLISTS ? selectedChecklistsCategories :
                                    activeTab === TABS.PRINT_TEMPLATES ? selectedPrintTemplatesCategories :
                                    activeTab === TABS.STATS ? selectedStatsCategories :
                                    activeTab === TABS.CONSTANTS ? selectedConstantsCategories : selectedOrganizationCategories;
  const currentToggleCategory = activeTab === TABS.SALES ? toggleSalesCategory :
                                activeTab === TABS.SUPPLIES ? toggleSuppliesCategory :
                                activeTab === TABS.PRODUCTS ? toggleProductsCategory :
                                activeTab === TABS.CATEGORIES ? toggleCategoriesCategory :
                                activeTab === TABS.SUPPLIERS ? toggleSuppliersCategory :
                                activeTab === TABS.STORES ? toggleStoresCategory :
                                activeTab === TABS.USERS ? toggleUsersCategory :
                                activeTab === TABS.TRANSACTIONS ? toggleTransactionsCategory :
                                activeTab === TABS.RETURNS ? toggleReturnsCategory :
                                activeTab === TABS.TRANSFERS ? toggleTransfersCategory :
                                activeTab === TABS.REVISIONS ? toggleRevisionsCategory :
                                activeTab === TABS.WRITEOFFS ? toggleWriteoffsCategory :
                                activeTab === TABS.SPENDINGS ? toggleSpendingsCategory :
                                activeTab === TABS.CLIENTS ? toggleClientsCategory :
                                activeTab === TABS.MANUFACTURES ? toggleManufacturesCategory :
                                activeTab === TABS.WORKING_HOURS ? toggleWorkingHoursCategory :
                                activeTab === TABS.CASHBOX ? toggleCashboxCategory :
                                activeTab === TABS.SAVED_CARTS ? toggleSavedCartsCategory :
                                activeTab === TABS.SUPPLY_ORDERS ? toggleSupplyOrdersCategory :
                                activeTab === TABS.CHECKLISTS ? toggleChecklistsCategory :
                                activeTab === TABS.PRINT_TEMPLATES ? togglePrintTemplatesCategory :
                                activeTab === TABS.STATS ? toggleStatsCategory :
                                activeTab === TABS.CONSTANTS ? toggleConstantsCategory : toggleOrganizationCategory;
  const currentVariations = activeTab === TABS.SALES ? TEST_VARIATIONS :
                            activeTab === TABS.SUPPLIES ? SUPPLY_TEST_VARIATIONS :
                            activeTab === TABS.PRODUCTS ? PRODUCT_TEST_VARIATIONS :
                            activeTab === TABS.CATEGORIES ? PRODUCT_CATEGORY_TEST_VARIATIONS :
                            activeTab === TABS.SUPPLIERS ? SUPPLIER_TEST_VARIATIONS :
                            activeTab === TABS.STORES ? STORE_TEST_VARIATIONS :
                            activeTab === TABS.USERS ? USER_TEST_VARIATIONS :
                            activeTab === TABS.TRANSACTIONS ? TRANSACTION_TEST_VARIATIONS :
                            activeTab === TABS.RETURNS ? RETURN_TEST_VARIATIONS :
                            activeTab === TABS.TRANSFERS ? TRANSFER_TEST_VARIATIONS :
                            activeTab === TABS.REVISIONS ? REVISION_TEST_VARIATIONS :
                            activeTab === TABS.WRITEOFFS ? WRITEOFF_TEST_VARIATIONS :
                            activeTab === TABS.SPENDINGS ? SPENDING_TEST_VARIATIONS :
                            activeTab === TABS.CLIENTS ? CLIENT_TEST_VARIATIONS :
                            activeTab === TABS.MANUFACTURES ? MANUFACTURE_TEST_VARIATIONS :
                            activeTab === TABS.WORKING_HOURS ? WORKING_HOURS_TEST_VARIATIONS :
                            activeTab === TABS.CASHBOX ? CASHBOX_TRANSACTIONS_TEST_VARIATIONS :
                            activeTab === TABS.SAVED_CARTS ? SAVED_CARTS_TEST_VARIATIONS :
                            activeTab === TABS.SUPPLY_ORDERS ? SUPPLY_ORDERS_TEST_VARIATIONS :
                            activeTab === TABS.CHECKLISTS ? CHECKLISTS_TEST_VARIATIONS :
                            activeTab === TABS.PRINT_TEMPLATES ? PRINT_TEMPLATES_TEST_VARIATIONS :
                            activeTab === TABS.STATS ? STATS_TEST_VARIATIONS :
                            activeTab === TABS.CONSTANTS ? CONSTANTS_TEST_VARIATIONS : ORGANIZATION_TEST_VARIATIONS;
  const currentSelectedCount = activeTab === TABS.SALES ? selectedSalesCount :
                               activeTab === TABS.SUPPLIES ? selectedSuppliesCount :
                               activeTab === TABS.PRODUCTS ? selectedProductsCount :
                               activeTab === TABS.CATEGORIES ? selectedCategoriesCount :
                               activeTab === TABS.SUPPLIERS ? selectedSuppliersCount :
                               activeTab === TABS.STORES ? selectedStoresCount :
                               activeTab === TABS.USERS ? selectedUsersCount :
                               activeTab === TABS.TRANSACTIONS ? selectedTransactionsCount :
                               activeTab === TABS.RETURNS ? selectedReturnsCount :
                               activeTab === TABS.TRANSFERS ? selectedTransfersCount :
                               activeTab === TABS.REVISIONS ? selectedRevisionsCount :
                               activeTab === TABS.WRITEOFFS ? selectedWriteoffsCount :
                               activeTab === TABS.SPENDINGS ? selectedSpendingsCount :
                               activeTab === TABS.CLIENTS ? selectedClientsCount :
                               activeTab === TABS.MANUFACTURES ? selectedManufacturesCount :
                               activeTab === TABS.WORKING_HOURS ? selectedWorkingHoursCount :
                               activeTab === TABS.CASHBOX ? selectedCashboxCount :
                               activeTab === TABS.SAVED_CARTS ? selectedSavedCartsCount :
                               activeTab === TABS.SUPPLY_ORDERS ? selectedSupplyOrdersCount :
                               activeTab === TABS.CHECKLISTS ? selectedChecklistsCount :
                               activeTab === TABS.PRINT_TEMPLATES ? selectedPrintTemplatesCount :
                               activeTab === TABS.STATS ? selectedStatsCount :
                               activeTab === TABS.CONSTANTS ? selectedConstantsCount : selectedOrganizationCount;

  return (
    <ScrollView style={[styles.container, { backgroundColor: theme['background-basic-color-2'] }]} testID="dual-write-test-screen">
      <View style={[styles.header, { backgroundColor: theme['background-basic-color-1'], borderBottomColor: theme['border-basic-color-3'] }]} testID="dual-write-header">
        <Text style={[styles.title, { color: theme['text-basic-color'] }]}>Dual Write Test Suite</Text>
        <Text style={[styles.subtitle, { color: theme['text-hint-color'] }]}>
          Verify Firestore-Supabase synchronization
        </Text>
      </View>

      {/* Organization Warning Banner */}
      <View style={[
        styles.orgBanner,
        (organization?.name?.toLowerCase().includes('test') || organization?.name?.toLowerCase().includes('тест')) ? styles.orgBannerSafe : styles.orgBannerWarning
      ]} testID="dual-write-org-banner">
        <Text style={styles.orgBannerTitle}>
          {(organization?.name?.toLowerCase().includes('test') || organization?.name?.toLowerCase().includes('тест')) ? '✓ Test Organization' : '⚠️ WARNING: Production Organization'}
        </Text>
        <Text style={[styles.orgBannerText, { color: theme['text-basic-color'] }]}>
          {organization?.name} ({organization?.id})
        </Text>
        {!(organization?.name?.toLowerCase().includes('test') || organization?.name?.toLowerCase().includes('тест')) && (
          <Text style={styles.orgBannerWarningText}>
            Switch to a test organization before running tests!
          </Text>
        )}
      </View>

      {/* Tab Bar */}
      <View style={[styles.tabBar, { backgroundColor: theme['background-basic-color-1'], borderBottomColor: theme['border-basic-color-3'] }]} testID="dual-write-tab-bar">
        <TouchableOpacity
          style={[styles.tab, { backgroundColor: theme['background-basic-color-2'], borderColor: theme['border-basic-color-3'] }, activeTab === TABS.SALES && styles.activeTab]}
          onPress={() => setActiveTab(TABS.SALES)}
          disabled={isRunning}
          testID="dual-write-tab-sales"
        >
          <Text style={[styles.tabText, { color: theme['text-hint-color'] }, activeTab === TABS.SALES && styles.activeTabText]}>
            Sales
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, { backgroundColor: theme['background-basic-color-2'], borderColor: theme['border-basic-color-3'] }, activeTab === TABS.SUPPLIES && styles.activeTab]}
          onPress={() => setActiveTab(TABS.SUPPLIES)}
          disabled={isRunning}
          testID="dual-write-tab-supplies"
        >
          <Text style={[styles.tabText, { color: theme['text-hint-color'] }, activeTab === TABS.SUPPLIES && styles.activeTabText]}>
            Supplies
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, { backgroundColor: theme['background-basic-color-2'], borderColor: theme['border-basic-color-3'] }, activeTab === TABS.PRODUCTS && styles.activeTab]}
          onPress={() => setActiveTab(TABS.PRODUCTS)}
          disabled={isRunning}
          testID="dual-write-tab-products"
        >
          <Text style={[styles.tabText, { color: theme['text-hint-color'] }, activeTab === TABS.PRODUCTS && styles.activeTabText]}>
            Products
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, { backgroundColor: theme['background-basic-color-2'], borderColor: theme['border-basic-color-3'] }, activeTab === TABS.CATEGORIES && styles.activeTab]}
          onPress={() => setActiveTab(TABS.CATEGORIES)}
          disabled={isRunning}
          testID="dual-write-tab-categories"
        >
          <Text style={[styles.tabText, { color: theme['text-hint-color'] }, activeTab === TABS.CATEGORIES && styles.activeTabText]}>
            Categories
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, { backgroundColor: theme['background-basic-color-2'], borderColor: theme['border-basic-color-3'] }, activeTab === TABS.SUPPLIERS && styles.activeTab]}
          onPress={() => setActiveTab(TABS.SUPPLIERS)}
          disabled={isRunning}
          testID="dual-write-tab-suppliers"
        >
          <Text style={[styles.tabText, { color: theme['text-hint-color'] }, activeTab === TABS.SUPPLIERS && styles.activeTabText]}>
            Suppliers
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, { backgroundColor: theme['background-basic-color-2'], borderColor: theme['border-basic-color-3'] }, activeTab === TABS.STORES && styles.activeTab]}
          onPress={() => setActiveTab(TABS.STORES)}
          disabled={isRunning}
          testID="dual-write-tab-stores"
        >
          <Text style={[styles.tabText, { color: theme['text-hint-color'] }, activeTab === TABS.STORES && styles.activeTabText]}>
            Stores
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, { backgroundColor: theme['background-basic-color-2'], borderColor: theme['border-basic-color-3'] }, activeTab === TABS.USERS && styles.activeTab]}
          onPress={() => setActiveTab(TABS.USERS)}
          disabled={isRunning}
          testID="dual-write-tab-users"
        >
          <Text style={[styles.tabText, { color: theme['text-hint-color'] }, activeTab === TABS.USERS && styles.activeTabText]}>
            Users
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, { backgroundColor: theme['background-basic-color-2'], borderColor: theme['border-basic-color-3'] }, activeTab === TABS.TRANSACTIONS && styles.activeTab]}
          onPress={() => setActiveTab(TABS.TRANSACTIONS)}
          disabled={isRunning}
          testID="dual-write-tab-transactions"
        >
          <Text style={[styles.tabText, { color: theme['text-hint-color'] }, activeTab === TABS.TRANSACTIONS && styles.activeTabText]}>
            Txns
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, { backgroundColor: theme['background-basic-color-2'], borderColor: theme['border-basic-color-3'] }, activeTab === TABS.RETURNS && styles.activeTab]}
          onPress={() => setActiveTab(TABS.RETURNS)}
          disabled={isRunning}
          testID="dual-write-tab-returns"
        >
          <Text style={[styles.tabText, { color: theme['text-hint-color'] }, activeTab === TABS.RETURNS && styles.activeTabText]}>
            Returns
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, { backgroundColor: theme['background-basic-color-2'], borderColor: theme['border-basic-color-3'] }, activeTab === TABS.TRANSFERS && styles.activeTab]}
          onPress={() => setActiveTab(TABS.TRANSFERS)}
          disabled={isRunning}
          testID="dual-write-tab-transfers"
        >
          <Text style={[styles.tabText, { color: theme['text-hint-color'] }, activeTab === TABS.TRANSFERS && styles.activeTabText]}>
            Transfers
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, { backgroundColor: theme['background-basic-color-2'], borderColor: theme['border-basic-color-3'] }, activeTab === TABS.REVISIONS && styles.activeTab]}
          onPress={() => setActiveTab(TABS.REVISIONS)}
          disabled={isRunning}
          testID="dual-write-tab-revisions"
        >
          <Text style={[styles.tabText, { color: theme['text-hint-color'] }, activeTab === TABS.REVISIONS && styles.activeTabText]}>
            Revisions
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, { backgroundColor: theme['background-basic-color-2'], borderColor: theme['border-basic-color-3'] }, activeTab === TABS.WRITEOFFS && styles.activeTab]}
          onPress={() => setActiveTab(TABS.WRITEOFFS)}
          disabled={isRunning}
          testID="dual-write-tab-writeoffs"
        >
          <Text style={[styles.tabText, { color: theme['text-hint-color'] }, activeTab === TABS.WRITEOFFS && styles.activeTabText]}>
            Writeoffs
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, { backgroundColor: theme['background-basic-color-2'], borderColor: theme['border-basic-color-3'] }, activeTab === TABS.SPENDINGS && styles.activeTab]}
          onPress={() => setActiveTab(TABS.SPENDINGS)}
          disabled={isRunning}
          testID="dual-write-tab-spendings"
        >
          <Text style={[styles.tabText, { color: theme['text-hint-color'] }, activeTab === TABS.SPENDINGS && styles.activeTabText]}>
            Spendings
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, { backgroundColor: theme['background-basic-color-2'], borderColor: theme['border-basic-color-3'] }, activeTab === TABS.CLIENTS && styles.activeTab]}
          onPress={() => setActiveTab(TABS.CLIENTS)}
          disabled={isRunning}
          testID="dual-write-tab-clients"
        >
          <Text style={[styles.tabText, { color: theme['text-hint-color'] }, activeTab === TABS.CLIENTS && styles.activeTabText]}>
            Clients
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, { backgroundColor: theme['background-basic-color-2'], borderColor: theme['border-basic-color-3'] }, activeTab === TABS.MANUFACTURES && styles.activeTab]}
          onPress={() => setActiveTab(TABS.MANUFACTURES)}
          disabled={isRunning}
          testID="dual-write-tab-manufactures"
        >
          <Text style={[styles.tabText, { color: theme['text-hint-color'] }, activeTab === TABS.MANUFACTURES && styles.activeTabText]}>
            Manufactures
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, { backgroundColor: theme['background-basic-color-2'], borderColor: theme['border-basic-color-3'] }, activeTab === TABS.WORKING_HOURS && styles.activeTab]}
          onPress={() => setActiveTab(TABS.WORKING_HOURS)}
          disabled={isRunning}
          testID="dual-write-tab-working-hours"
        >
          <Text style={[styles.tabText, { color: theme['text-hint-color'] }, activeTab === TABS.WORKING_HOURS && styles.activeTabText]}>
            Working Hours
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, { backgroundColor: theme['background-basic-color-2'], borderColor: theme['border-basic-color-3'] }, activeTab === TABS.CASHBOX && styles.activeTab]}
          onPress={() => setActiveTab(TABS.CASHBOX)}
          disabled={isRunning}
          testID="dual-write-tab-cashbox"
        >
          <Text style={[styles.tabText, { color: theme['text-hint-color'] }, activeTab === TABS.CASHBOX && styles.activeTabText]}>
            Cashbox
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, { backgroundColor: theme['background-basic-color-2'], borderColor: theme['border-basic-color-3'] }, activeTab === TABS.SAVED_CARTS && styles.activeTab]}
          onPress={() => setActiveTab(TABS.SAVED_CARTS)}
          disabled={isRunning}
          testID="dual-write-tab-saved-carts"
        >
          <Text style={[styles.tabText, { color: theme['text-hint-color'] }, activeTab === TABS.SAVED_CARTS && styles.activeTabText]}>
            Saved Carts
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, { backgroundColor: theme['background-basic-color-2'], borderColor: theme['border-basic-color-3'] }, activeTab === TABS.SUPPLY_ORDERS && styles.activeTab]}
          onPress={() => setActiveTab(TABS.SUPPLY_ORDERS)}
          disabled={isRunning}
          testID="dual-write-tab-supply-orders"
        >
          <Text style={[styles.tabText, { color: theme['text-hint-color'] }, activeTab === TABS.SUPPLY_ORDERS && styles.activeTabText]}>
            Supply Orders
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, { backgroundColor: theme['background-basic-color-2'], borderColor: theme['border-basic-color-3'] }, activeTab === TABS.CHECKLISTS && styles.activeTab]}
          onPress={() => setActiveTab(TABS.CHECKLISTS)}
          disabled={isRunning}
          testID="dual-write-tab-checklists"
        >
          <Text style={[styles.tabText, { color: theme['text-hint-color'] }, activeTab === TABS.CHECKLISTS && styles.activeTabText]}>
            Checklists
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, { backgroundColor: theme['background-basic-color-2'], borderColor: theme['border-basic-color-3'] }, activeTab === TABS.PRINT_TEMPLATES && styles.activeTab]}
          onPress={() => setActiveTab(TABS.PRINT_TEMPLATES)}
          disabled={isRunning}
          testID="dual-write-tab-print-templates"
        >
          <Text style={[styles.tabText, { color: theme['text-hint-color'] }, activeTab === TABS.PRINT_TEMPLATES && styles.activeTabText]}>
            Templates
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, { backgroundColor: theme['background-basic-color-2'], borderColor: theme['border-basic-color-3'] }, activeTab === TABS.CONSTANTS && styles.activeTab]}
          onPress={() => setActiveTab(TABS.CONSTANTS)}
          disabled={isRunning}
          testID="dual-write-tab-constants"
        >
          <Text style={[styles.tabText, { color: theme['text-hint-color'] }, activeTab === TABS.CONSTANTS && styles.activeTabText]}>
            Constants
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, { backgroundColor: theme['background-basic-color-2'], borderColor: theme['border-basic-color-3'] }, activeTab === TABS.ORGANIZATIONS && styles.activeTab]}
          onPress={() => setActiveTab(TABS.ORGANIZATIONS)}
          disabled={isRunning}
          testID="dual-write-tab-organizations"
        >
          <Text style={[styles.tabText, { color: theme['text-hint-color'] }, activeTab === TABS.ORGANIZATIONS && styles.activeTabText]}>
            Org
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, { backgroundColor: theme['background-basic-color-2'], borderColor: theme['border-basic-color-3'] }, activeTab === TABS.STATS && styles.activeTab]}
          onPress={() => setActiveTab(TABS.STATS)}
          disabled={isRunning}
          testID="dual-write-tab-stats"
        >
          <Text style={[styles.tabText, { color: theme['text-hint-color'] }, activeTab === TABS.STATS && styles.activeTabText]}>
            Stats
          </Text>
        </TouchableOpacity>
      </View>

      {/* How Tests Work */}
      <View style={[styles.section, { backgroundColor: theme['background-basic-color-1'] }]}>
        <Text style={[styles.sectionTitle, { color: theme['text-basic-color'] }]}>How Tests Work</Text>
        {activeTab === TABS.SALES && (
          <>
            <Text style={[styles.descriptionText, { color: theme['text-hint-color'] }]}>
              Each test creates a sale in Firestore, waits for dual-write sync, then compares data between Firestore and Supabase via the backend API. ALL fields must match for the test to pass.
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Price & Totals:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} total_price, total_purchase_price, final_total_cart_price
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Discounts:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} discount_amount, discount_percentage{'\n'}
              {'\u2022'} total_discount, total_discount_percent
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Punch Card:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} punch_card_enabled, punch_card_capacity, punch_card_reduction
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Bonus & Cashback:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} bonus_accumulation, bonus_reduction, bonus_action{'\n'}
              {'\u2022'} cashback_enabled
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Payment:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} payment_method, second_payment_method{'\n'}
              {'\u2022'} first_payment_method_value, second_payment_method_value{'\n'}
              {'\u2022'} purchase_method
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>⭐ Cascading Writes (P0):</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} product_movements (type = sale){'\n'}
              {'\u2022'} product_store_stock (decrement){'\n'}
              {'\u2022'} transactions (if paid){'\n'}
              {'\u2022'} daily_stats updates{'\n'}
              {'\u2022'} client stats updates (bonus, punch cards)
            </Text>
          </>
        )}
        {activeTab === TABS.SUPPLIES && (
          <>
            <Text style={[styles.descriptionText, { color: theme['text-hint-color'] }]}>
              Each test creates a supply in Firestore, waits for dual-write sync, then compares data between Firestore and Supabase. ALL fields must match for the test to pass.
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Items:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} items array, quantity, return_quantity, purchase_price
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Totals:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} total_sum, total_price
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Payment:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} is_paid, payment_method, payment_date
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Supplier:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} supplier_id, supplier_name
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Optional:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} comment, supply_order_id, invoice_image
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>⭐ Cascading Writes (P0):</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} product_movements (type = supply){'\n'}
              {'\u2022'} product_store_stock (increment){'\n'}
              {'\u2022'} transactions (if paid){'\n'}
              {'\u2022'} daily_stats updates{'\n'}
              {'\u2022'} supplier stats updates
            </Text>
          </>
        )}
        {activeTab === TABS.CONSTANTS && (
          <>
            <Text style={[styles.descriptionText, { color: theme['text-hint-color'] }]}>
              Each test creates or updates a constant item in Firestore, waits for dual-write sync, then compares data between Firestore and Supabase. Tests cover create, update, and delete operations.
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Constant Types:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} Wallets (name, icon){'\n'}
              {'\u2022'} Payment Methods (name, isCash, commission){'\n'}
              {'\u2022'} Roles (name, salary, permissions){'\n'}
              {'\u2022'} Product Units (name, shortName)
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Other Constants:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} Spending Categories{'\n'}
              {'\u2022'} Discount Options{'\n'}
              {'\u2022'} Write-off Reasons{'\n'}
              {'\u2022'} Return Reasons{'\n'}
              {'\u2022'} Purchase Methods
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Operations Tested:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} Create new constant item{'\n'}
              {'\u2022'} Update existing constant{'\n'}
              {'\u2022'} Delete constant item
            </Text>
          </>
        )}
        {activeTab === TABS.ORGANIZATIONS && (
          <>
            <Text style={[styles.descriptionText, { color: theme['text-hint-color'] }]}>
              Each test updates an organization setting in Firestore, waits for dual-write sync, then compares data between Firestore and Supabase.
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Module Settings:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} Loyalty module (enable/disable){'\n'}
              {'\u2022'} Marketplace module{'\n'}
              {'\u2022'} Reports module{'\n'}
              {'\u2022'} Multiple modules at once
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Marketplace & Link:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} Set/clear store for marketplace{'\n'}
              {'\u2022'} Enable/disable link
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Stock & Basic Info:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} Out-of-stock product hiding{'\n'}
              {'\u2022'} Organization name, description{'\n'}
              {'\u2022'} Phone, address{'\n'}
              {'\u2022'} Currency settings (USD, EUR)
            </Text>
          </>
        )}
        {activeTab === TABS.STATS && (
          <>
            <Text style={[styles.descriptionText, { color: theme['text-hint-color'] }]}>
              Stats tests create fresh operations (sales/supplies), wait for sync, then compare daily_stats and product_stats between Firestore and Supabase. This "Fresh Test" approach ensures accurate comparisons of newly triggered stats updates.
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Test Approach:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} Creates test sale/supply via dual-write{'\n'}
              {'\u2022'} Waits 6s for Supabase sync{'\n'}
              {'\u2022'} Compares stats in both databases{'\n'}
              {'\u2022'} Cleans up test data after comparison
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Daily Stats Verified:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} Sales count & total{'\n'}
              {'\u2022'} Supplies count & total{'\n'}
              {'\u2022'} Returns, writeoffs, transfers{'\n'}
              {'\u2022'} Bonus accumulation/reduction
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Product Stats Verified:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} Sold quantity & amount{'\n'}
              {'\u2022'} Supplied quantity & amount
            </Text>
          </>
        )}
        {activeTab === TABS.PRODUCTS && (
          <>
            <Text style={[styles.descriptionText, { color: theme['text-hint-color'] }]}>
              Each test creates or updates a product in Firestore, waits for dual-write sync, then compares data between Firestore and Supabase via the backend API. ALL fields must match for the test to pass.
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Basic Fields:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} name, description, sku, barcode{'\n'}
              {'\u2022'} base_purchase_price, package_price{'\n'}
              {'\u2022'} base_unit, track_inventory
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Relations:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} category_id, supplier_id{'\n'}
              {'\u2022'} Reference resolution via firestore_id
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Store Prices:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} storePrices (Firestore) → product_store_prices (Supabase){'\n'}
              {'\u2022'} Per-store pricing with price_type
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Product Types:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} product_type: 'product' vs 'service'{'\n'}
              {'\u2022'} is_composite with ingredients array{'\n'}
              {'\u2022'} manufactured_before_sale flag
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Inventory:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} storeStock (Firestore) → product_store_stock (Supabase){'\n'}
              {'\u2022'} active_in_store settings
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Lifecycle:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} Update product fields{'\n'}
              {'\u2022'} Update prices, stock manually{'\n'}
              {'\u2022'} Delete product
            </Text>
          </>
        )}
        {activeTab === TABS.CATEGORIES && (
          <>
            <Text style={[styles.descriptionText, { color: theme['text-hint-color'] }]}>
              Each test creates or updates a product category in Firestore, waits for dual-write sync, then compares data between Firestore and Supabase via the backend API. ALL fields must match for the test to pass.
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Basic Fields:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} name, description, color{'\n'}
              {'\u2022'} parent_category_id (hierarchical structure){'\n'}
              {'\u2022'} display_order, level, path
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Lifecycle:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} Update category name, description{'\n'}
              {'\u2022'} Delete category
            </Text>
          </>
        )}
        {activeTab === TABS.SUPPLIERS && (
          <>
            <Text style={[styles.descriptionText, { color: theme['text-hint-color'] }]}>
              Each test creates or updates a supplier in Firestore, waits for dual-write sync, then compares data between Firestore and Supabase via the backend API. ALL fields must match for the test to pass.
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Basic Fields:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} name (required){'\n'}
              {'\u2022'} phone, email, address{'\n'}
              {'\u2022'} contact_person, payment_terms{'\n'}
              {'\u2022'} notes, active
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Lifecycle:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} Update supplier fields{'\n'}
              {'\u2022'} Delete supplier
            </Text>
          </>
        )}
        {activeTab === TABS.STORES && (
          <>
            <Text style={[styles.descriptionText, { color: theme['text-hint-color'] }]}>
              Each test creates or updates a store in Firestore, waits for dual-write sync, then compares data between Firestore and Supabase via the backend API. ALL fields must match for the test to pass.
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Basic Fields:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} name (required){'\n'}
              {'\u2022'} timezone, businessDayCutoffTime{'\n'}
              {'\u2022'} inventoryCostingMethod (FIFO, LIFO, etc.){'\n'}
              {'\u2022'} address, autoCloseShiftTime, showOnTop
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Lifecycle:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} Update store fields{'\n'}
              {'\u2022'} Delete store
            </Text>
          </>
        )}
        {activeTab === TABS.USERS && (
          <>
            <Text style={[styles.descriptionText, { color: theme['text-hint-color'] }]}>
              Each test updates an existing user in Firestore, waits for dual-write sync, then compares data between Firestore and Supabase via the backend API. Tests do NOT create new auth users - only update existing user records.
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Basic Fields:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} name - User display name{'\n'}
              {'\u2022'} role - User role (admin, manager, cashier)
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Store Assignment:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} store - Assigned store reference{'\n'}
              {'\u2022'} Uses updateUserStore() function
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Note:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} Tests use the currently logged-in user{'\n'}
              {'\u2022'} Original values are restored after each test{'\n'}
              {'\u2022'} Store test requires 2+ stores in organization
            </Text>
          </>
        )}
        {activeTab === TABS.TRANSACTIONS && (
          <>
            <Text style={[styles.descriptionText, { color: theme['text-hint-color'] }]}>
              Each test creates a transaction in Firestore, waits for dual-write sync, then compares data between Firestore and Supabase via the backend API. ALL fields must match for the test to pass.
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Transaction Types:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} income - Money coming in to a wallet{'\n'}
              {'\u2022'} expense - Money going out from a wallet{'\n'}
              {'\u2022'} transfer - Money moving between wallets
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Create Tests:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} T1: Create income transaction{'\n'}
              {'\u2022'} T2: Create expense transaction
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Delete Test:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} T3: Create, verify, then delete transaction
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Fields Compared:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} type, value, source_wallet, destination_wallet
            </Text>
          </>
        )}
        {activeTab === TABS.RETURNS && (
          <>
            <Text style={[styles.descriptionText, { color: theme['text-hint-color'] }]}>
              Each test creates a sale return in Firestore, waits for dual-write sync, then compares data between Firestore and Supabase via the backend API. Returns are created on existing sales.
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Return Types:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} type 0 - Exchange (no money refund){'\n'}
              {'\u2022'} type 1 - Money back refund
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Create Tests:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} R1: Create return (money back, stock update){'\n'}
              {'\u2022'} R2: Create return (exchange, no stock update)
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Delete Test:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} R3: Create, verify, then delete return
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Fields Compared:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} sale_id, returned_items, total_refund{'\n'}
              {'\u2022'} reason, firestore_id
            </Text>
          </>
        )}
        {activeTab === TABS.TRANSFERS && (
          <>
            <Text style={[styles.descriptionText, { color: theme['text-hint-color'] }]}>
              Each test creates a stock transfer between two stores in Firestore, waits for dual-write sync, then compares data between Firestore and Supabase via the backend API. Requires at least 2 stores.
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Create Tests:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} T1: Single item transfer{'\n'}
              {'\u2022'} T2: Multiple items transfer
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Delete Test:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} T3: Create, verify, then delete transfer
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Fields Compared:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} from_store_id, to_store_id, items{'\n'}
              {'\u2022'} amount, transfer_date, firestore_id
            </Text>
          </>
        )}
        {activeTab === TABS.REVISIONS && (
          <>
            <Text style={[styles.descriptionText, { color: theme['text-hint-color'] }]}>
              Each test creates a stock revision (inventory count) in Firestore, waits for dual-write sync, then compares data between Firestore and Supabase via the backend API.
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Create Tests:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} V1: Create single product revision{'\n'}
              {'\u2022'} V2: Create multi-product revision
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Delete Test:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} V3: Create, verify, then delete revision
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Fields Compared:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} store_id, items, total_difference{'\n'}
              {'\u2022'} revision_date, firestore_id
            </Text>
          </>
        )}
        {activeTab === TABS.WRITEOFFS && (
          <>
            <Text style={[styles.descriptionText, { color: theme['text-hint-color'] }]}>
              Each test creates a stock writeoff in Firestore, waits for dual-write sync, then compares data between Firestore and Supabase via the backend API.
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Create Tests:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} W1: Create single product writeoff
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Delete Test:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} W2: Create, verify, then delete writeoff
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Fields Compared:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} store_id, items, total_amount{'\n'}
              {'\u2022'} writeoff_date, firestore_id
            </Text>
          </>
        )}
        {activeTab === TABS.SPENDINGS && (
          <>
            <Text style={[styles.descriptionText, { color: theme['text-hint-color'] }]}>
              Each test creates a spending (expense) in Firestore, waits for dual-write sync, then compares data between Firestore and Supabase via the backend API.
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Create Tests:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} S1: Create spending with payment source
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Delete Test:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} S2: Create, verify, then delete spending
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Fields Compared:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} store_id, name, amount, category{'\n'}
              {'\u2022'} payment_method, spending_date, firestore_id
            </Text>
          </>
        )}
        {activeTab === TABS.CLIENTS && (
          <>
            <Text style={[styles.descriptionText, { color: theme['text-hint-color'] }]}>
              Each test creates a client in Firestore, waits for dual-write sync, then compares data between Firestore and Supabase via the backend API.
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Create Tests:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} C1: Create client with name only{'\n'}
              {'\u2022'} C2: Create client with phone{'\n'}
              {'\u2022'} C3: Create client with email
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Update Test:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} C4: Create, update name, verify sync
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Delete Test:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} C5: Create, verify, then delete client
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Fields Compared:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} name, phone, email{'\n'}
              {'\u2022'} organization_id, firestore_id
            </Text>
          </>
        )}
        {activeTab === TABS.MANUFACTURES && (
          <>
            <Text style={[styles.descriptionText, { color: theme['text-hint-color'] }]}>
              Each test creates a manufacture in Firestore, waits for dual-write sync, then compares data between Firestore and Supabase via the backend API.
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Create Test:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} M1: Create manufacture (composite product)
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Delete Test:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} M2: Create, verify, then delete manufacture
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Fields Compared:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} quantity, total_sum, ingredients{'\n'}
              {'\u2022'} product_id, store_id, firestore_id
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Prerequisites:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} Requires a composite product with ingredients
            </Text>
          </>
        )}
        {activeTab === TABS.WORKING_HOURS && (
          <>
            <Text style={[styles.descriptionText, { color: theme['text-hint-color'] }]}>
              Each test creates working hours in Firestore, waits for dual-write sync, then compares data between Firestore and Supabase via the backend API.
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Create Test:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} WH1: Create working hours (hourly)
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Update Test:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} WH2: Create, update hours, verify sync
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Delete Test:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} WH3: Create, verify, then delete working hours
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Fields Compared:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} amount, salary_type, employee_id{'\n'}
              {'\u2022'} store_id, firestore_id
            </Text>
          </>
        )}
        {activeTab === TABS.CASHBOX && (
          <>
            <Text style={[styles.descriptionText, { color: theme['text-hint-color'] }]}>
              Each test creates cashbox transactions in Firestore, waits for dual-write sync, then compares data between Firestore and Supabase via the backend API.
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Create Test:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} CBT1: Create cashbox transaction (add cash)
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Update Test:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} CBT2: Create, update amount, verify sync
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Delete Test:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} CBT3: Create, verify, then delete transaction
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Fields Compared:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} amount, type, wallet{'\n'}
              {'\u2022'} store_id, firestore_id
            </Text>
          </>
        )}
        {activeTab === TABS.SAVED_CARTS && (
          <>
            <Text style={[styles.descriptionText, { color: theme['text-hint-color'] }]}>
              Each test creates saved carts in Firestore, waits for dual-write sync, then compares data between Firestore and Supabase via the backend API.
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Create Test:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} SC1: Create saved cart with items
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Update Test:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} SC2: Create, update name, verify sync
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Delete Test:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} SC3: Create, verify, then delete saved cart
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Fields Compared:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} name, items (count){'\n'}
              {'\u2022'} store_id, organization_id
            </Text>
          </>
        )}
        {activeTab === TABS.SUPPLY_ORDERS && (
          <>
            <Text style={[styles.descriptionText, { color: theme['text-hint-color'] }]}>
              Each test creates supply orders in Firestore, waits for dual-write sync, then compares data between Firestore and Supabase via the backend API.
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Create Test:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} SO1: Create supply order with items
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Update Test:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} SO2: Create, update status, verify sync
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Delete Test:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} SO3: Create, verify, then delete supply order
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Fields Compared:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} status, items (count){'\n'}
              {'\u2022'} store_id, supplier_id, firestore_id
            </Text>
          </>
        )}
        {activeTab === TABS.CHECKLISTS && (
          <>
            <Text style={[styles.descriptionText, { color: theme['text-hint-color'] }]}>
              Each test creates checklists in Firestore, waits for dual-write sync, then compares data between Firestore and Supabase via the backend API.
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Create Test:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} CL1: Create checklist with tasks
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Update Test:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} CL2: Create, update content, verify sync
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Fields Compared:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} checklist_type, content (length){'\n'}
              {'\u2022'} store_id, firestore_id
            </Text>
          </>
        )}
        {activeTab === TABS.PRINT_TEMPLATES && (
          <>
            <Text style={[styles.descriptionText, { color: theme['text-hint-color'] }]}>
              Each test creates print templates in Firestore, waits for dual-write sync, then compares data between Firestore and Supabase via the backend API.
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Create Test:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} PT1: Create receipt template with content
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Update Test:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} PT2: Create, update name/content, verify sync
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Delete Test:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} PT3: Create, delete, verify removal
            </Text>
            <Text style={[styles.descriptionSubtitle, { color: theme['text-basic-color'] }]}>Fields Compared:</Text>
            <Text style={[styles.descriptionList, { color: theme['text-hint-color'] }]}>
              {'\u2022'} name, type, is_default{'\n'}
              {'\u2022'} stores, firestore_id
            </Text>
          </>
        )}
      </View>

      {/* Test Configuration Info */}
      <View style={[styles.section, { backgroundColor: theme['background-basic-color-1'] }]}>
        <Text style={[styles.sectionTitle, { color: theme['text-basic-color'] }]}>Test Configuration</Text>
        <View style={styles.configRow}>
          <Text style={[styles.configLabel, { color: theme['text-hint-color'] }]}>Organization:</Text>
          <Text style={[styles.configValue, { color: theme['text-basic-color'] }]}>{testConfig.organizationName}</Text>
        </View>
        <View style={styles.configRow}>
          <Text style={[styles.configLabel, { color: theme['text-hint-color'] }]}>Target Store:</Text>
          <Text style={[styles.configValue, { color: theme['text-basic-color'] }]}>{testConfig.storeName}</Text>
        </View>
        <View style={styles.configRow}>
          <Text style={[styles.configLabel, { color: theme['text-hint-color'] }]}>Store ID:</Text>
          <Text style={[styles.configValue, styles.configMono, { color: theme['text-basic-color'] }]}>{testConfig.storeId || 'none'}</Text>
        </View>
        <View style={styles.configRow}>
          <Text style={[styles.configLabel, { color: theme['text-hint-color'] }]}>Products:</Text>
          <Text style={[styles.configValue, { color: theme['text-basic-color'] }]}>{testConfig.productCount}</Text>
        </View>
        <View style={styles.configRow}>
          <Text style={[styles.configLabel, { color: theme['text-hint-color'] }]}>Payment Methods:</Text>
          <Text style={[
            styles.configValue, { color: theme['text-basic-color'] },
            !testConfig.hasValidPaymentMethods && styles.configError,
          ]}>
            {testConfig.paymentMethodCount} / {testConfig.totalPaymentMethods}
            {!testConfig.hasValidPaymentMethods && ' (none for this store)'}
          </Text>
        </View>
        {!testConfig.hasValidPaymentMethods && testConfig.configuredStoreIds.length > 0 && (
          <View style={styles.configDebug}>
            <Text style={styles.configDebugLabel}>Payment methods configured for stores:</Text>
            <Text style={[styles.configDebugValue, { color: theme['text-basic-color'] }]}>{testConfig.configuredStoreIds.join(', ')}</Text>
          </View>
        )}
      </View>

      {/* Category Selection */}
      <View style={[styles.section, { backgroundColor: theme['background-basic-color-1'] }]} testID="dual-write-categories-section">
        <Text style={[styles.sectionTitle, { color: theme['text-basic-color'] }]}>Test Categories</Text>
        {currentCategories.map(cat => {
          const categoryTests = currentVariations.filter(v => v.category === cat.id);
          const isExpanded = expandedCategories[`${activeTab}-${cat.id}`];
          return (
            <View key={cat.id} testID={`dual-write-category-${cat.id}`}>
              <View style={styles.categoryRow}>
                <TouchableOpacity
                  style={styles.categoryLabelRow}
                  onPress={() => toggleExpanded(`${activeTab}-${cat.id}`)}
                  testID={`dual-write-category-expand-${cat.id}`}
                >
                  <Text style={[styles.categoryLabel, { color: theme['text-basic-color'] }]}>{cat.label}</Text>
                  <Text style={[styles.categoryCount, { color: theme['text-hint-color'] }]}>({cat.count})</Text>
                  <Text style={[styles.expandIcon, { color: theme['text-hint-color'] }]}>{isExpanded ? '▼' : '▶'}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.runCategoryButton,
                    isRunning && [styles.runCategoryButtonDisabled, { backgroundColor: theme['border-basic-color-3'] }],
                    runningCategory === cat.id && styles.runCategoryButtonRunning,
                  ]}
                  onPress={() => runCategoryTests(cat.id)}
                  disabled={isRunning}
                  testID={`dual-write-category-run-${cat.id}`}
                >
                  <Text style={styles.runCategoryButtonText}>
                    {runningCategory === cat.id ? '...' : '▶ Run'}
                  </Text>
                </TouchableOpacity>
              </View>
              {isExpanded && (
                <View style={[styles.testList, { borderLeftColor: theme['border-basic-color-3'] }]} testID={`dual-write-category-tests-${cat.id}`}>
                  {categoryTests.map(test => (
                    <View key={test.id} style={styles.testItem} testID={`dual-write-test-item-${test.id}`}>
                      <Text style={[styles.testId, { color: theme['text-hint-color'] }]}>{test.id}</Text>
                      <Text style={[styles.testName, { color: theme['text-hint-color'] }]}>{test.name}</Text>
                      <TouchableOpacity
                        style={[
                          styles.runTestButton,
                          isRunning && [styles.runTestButtonDisabled, { backgroundColor: theme['border-basic-color-3'] }],
                          runningTestId === test.id && styles.runTestButtonRunning,
                        ]}
                        onPress={() => runSpecificTest(test.id)}
                        disabled={isRunning}
                        testID={`dual-write-test-run-${test.id}`}
                      >
                        <Text style={styles.runTestButtonText}>
                          {runningTestId === test.id ? '...' : '▶'}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  ))}
                </View>
              )}
            </View>
          );
        })}
      </View>

      {/* Action Buttons */}
      <View style={styles.buttonContainer} testID="dual-write-actions">
        <TouchableOpacity
          style={[styles.button, styles.quickButton, isRunning && runningTestType !== 'quick' && styles.buttonDisabled]}
          onPress={() => runTests(true)}
          disabled={isRunning}
          testID="dual-write-btn-quick-test"
        >
          {runningTestType === 'quick' ? (
            <View style={styles.buttonContent}>
              <ActivityIndicator color="#fff" size="small" />
              <Text style={styles.buttonText}> Running...</Text>
            </View>
          ) : (
            <Text style={styles.buttonText}>Quick Test (4 tests)</Text>
          )}
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.button, styles.fullButton, isRunning && runningTestType !== 'full' && styles.buttonDisabled]}
          onPress={() => runTests(false)}
          disabled={isRunning}
          testID="dual-write-btn-full-test"
        >
          {runningTestType === 'full' ? (
            <View style={styles.buttonContent}>
              <ActivityIndicator color="#fff" size="small" />
              <Text style={styles.buttonText}> Running...</Text>
            </View>
          ) : (
            <Text style={styles.buttonText}>Full Test ({currentSelectedCount} tests)</Text>
          )}
        </TouchableOpacity>
      </View>

      {/* Results Summary */}
      {results && (
        <View style={[styles.section, { backgroundColor: theme['background-basic-color-1'] }]} testID="dual-write-results-section">
          <Text style={[styles.sectionTitle, { color: theme['text-basic-color'] }]}>Results</Text>
          <View style={styles.resultsRow} testID="dual-write-results-summary">
            <View style={[styles.resultBox, styles.passedBox]} testID="dual-write-results-passed">
              <Text style={[styles.resultNumber, { color: theme['text-basic-color'] }]}>{results.passed.length}</Text>
              <Text style={[styles.resultLabel, { color: theme['text-hint-color'] }]}>Passed</Text>
            </View>
            <View style={[styles.resultBox, styles.failedBox]} testID="dual-write-results-failed">
              <Text style={[styles.resultNumber, { color: theme['text-basic-color'] }]}>{results.failed.length}</Text>
              <Text style={[styles.resultLabel, { color: theme['text-hint-color'] }]}>Failed</Text>
            </View>
            <View style={[styles.resultBox, styles.skippedBox]} testID="dual-write-results-skipped">
              <Text style={[styles.resultNumber, { color: theme['text-basic-color'] }]}>{results.skipped.length}</Text>
              <Text style={[styles.resultLabel, { color: theme['text-hint-color'] }]}>Skipped</Text>
            </View>
          </View>

          {/* Failed Tests Details */}
          {results.failed.length > 0 && (
            <View style={styles.failedSection}>
              <Text style={styles.failedTitle}>Failed Tests:</Text>
              {results.failed.map((fail, index) => (
                <View key={index} style={styles.failedItem}>
                  <Text style={[styles.failedName, { color: theme['text-basic-color'] }]}>
                    {fail.variation.id}: {fail.variation.name}
                  </Text>
                  {fail.error && (
                    <Text style={styles.failedError}>Error: {fail.error}</Text>
                  )}
                  {fail.result?.differences?.map((diff, i) => (
                    <Text key={i} style={[styles.failedDiff, { color: theme['text-hint-color'] }]}>
                      • {diff.field}: {JSON.stringify(diff.supabaseValue)} vs {JSON.stringify(diff.firestoreValue)}
                    </Text>
                  ))}
                </View>
              ))}
            </View>
          )}
        </View>
      )}

      {/* Log Output */}
      {logs.length > 0 && (
        <View style={[styles.section, { backgroundColor: theme['background-basic-color-1'] }]} testID="dual-write-logs-section">
          <View style={styles.logHeader}>
            <Text style={[styles.sectionTitle, { color: theme['text-basic-color'] }]}>Log Output</Text>
            <TouchableOpacity style={styles.copyButton} onPress={copyLogs} testID="dual-write-btn-copy-logs">
              <Text style={styles.copyButtonText}>Copy Logs</Text>
            </TouchableOpacity>
          </View>
          <ScrollView style={styles.logContainer} nestedScrollEnabled testID="dual-write-logs-container">
            {logs.map((log, index) => (
              <Text
                key={index}
                style={[
                  styles.logLine,
                  log.type === 'error' && styles.logError,
                  log.type === 'warning' && styles.logWarning,
                ]}
              >
                [{log.timestamp}] {log.message}
              </Text>
            ))}
          </ScrollView>
        </View>
      )}

      <View style={styles.footer} />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    padding: 20,
    borderBottomWidth: 1,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  subtitle: {
    fontSize: 14,
    marginTop: 4,
  },
  orgBanner: {
    padding: 12,
    marginHorizontal: 12,
    marginTop: 12,
    marginBottom: 12,
    borderRadius: 8,
    borderWidth: 2,
  },
  orgBannerSafe: {
    backgroundColor: '#e8f5e9',
    borderColor: '#4CAF50',
  },
  orgBannerWarning: {
    backgroundColor: '#fff3e0',
    borderColor: '#ff9800',
  },
  orgBannerTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  orgBannerText: {
    fontSize: 14,
  },
  orgBannerWarningText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#e65100',
    marginTop: 8,
  },
  tabBar: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    borderBottomWidth: 1,
    paddingVertical: 8,
    paddingHorizontal: 8,
  },
  tab: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 16,
    margin: 4,
  },
  activeTab: {
    backgroundColor: '#4CAF50',
    borderColor: '#4CAF50',
  },
  tabText: {
    fontSize: 13,
    fontWeight: '500',
  },
  activeTabText: {
    color: '#fff',
  },
  section: {
    marginTop: 12,
    padding: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  descriptionText: {
    fontSize: 13,
    lineHeight: 18,
    marginBottom: 12,
  },
  descriptionSubtitle: {
    fontSize: 13,
    fontWeight: '600',
    marginTop: 8,
    marginBottom: 4,
  },
  descriptionList: {
    fontSize: 12,
    lineHeight: 18,
    paddingLeft: 8,
  },
  configRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 6,
  },
  configLabel: {
    fontSize: 14,
  },
  configValue: {
    fontSize: 14,
    fontWeight: '500',
  },
  configError: {
    color: '#C62828',
  },
  configWarning: {
    fontSize: 12,
    color: '#C62828',
    marginTop: 8,
    padding: 8,
    backgroundColor: '#FFEBEE',
    borderRadius: 4,
  },
  configMono: {
    fontFamily: 'monospace',
    fontSize: 12,
  },
  configDebug: {
    marginTop: 8,
    padding: 8,
    backgroundColor: '#FFF3E0',
    borderRadius: 4,
  },
  configDebugLabel: {
    fontSize: 11,
    color: '#E65100',
    marginBottom: 4,
  },
  configDebugValue: {
    fontSize: 11,
    fontFamily: 'monospace',
  },
  categoryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
  },
  categoryLabelRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  categoryLabel: {
    flex: 1,
    fontSize: 15,
  },
  categoryCount: {
    fontSize: 14,
    marginRight: 8,
  },
  expandIcon: {
    fontSize: 10,
    width: 16,
  },
  testList: {
    marginLeft: 16,
    marginBottom: 8,
    paddingLeft: 12,
    borderLeftWidth: 2,

  },
  testItem: {
    flexDirection: 'row',
    paddingVertical: 4,
  },
  testId: {
    width: 50,
    fontSize: 12,
    fontFamily: 'monospace',
  },
  testName: {
    flex: 1,
    fontSize: 13,
  },
  runTestButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#4CAF50',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 8,
  },
  runTestButtonDisabled: {
  },
  runTestButtonRunning: {
    backgroundColor: '#2196F3',
  },
  runTestButtonText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  runCategoryButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
    backgroundColor: '#2196F3',
    marginLeft: 8,
  },
  runCategoryButtonDisabled: {
  },
  runCategoryButtonRunning: {
    backgroundColor: '#FF9800',
  },
  runCategoryButtonText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  buttonContainer: {
    padding: 16,
    gap: 12,
  },
  button: {
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  quickButton: {
    backgroundColor: '#2196F3',
  },
  fullButton: {
    backgroundColor: '#4CAF50',
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  resultsRow: {
    flexDirection: 'row',
    gap: 12,
  },
  resultBox: {
    flex: 1,
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  passedBox: {
    backgroundColor: '#E8F5E9',
  },
  failedBox: {
    backgroundColor: '#FFEBEE',
  },
  skippedBox: {
    backgroundColor: '#FFF3E0',
  },
  resultNumber: {
    fontSize: 28,
    fontWeight: 'bold',
  },
  resultLabel: {
    fontSize: 12,
    marginTop: 4,
  },
  failedSection: {
    marginTop: 16,
    padding: 12,
    backgroundColor: '#FFEBEE',
    borderRadius: 8,
  },
  failedTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#C62828',
    marginBottom: 8,
  },
  failedItem: {
    marginBottom: 8,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#FFCDD2',
  },
  failedName: {
    fontSize: 13,
    fontWeight: '500',
  },
  failedError: {
    fontSize: 12,
    color: '#C62828',
    marginTop: 2,
  },
  failedDiff: {
    fontSize: 11,
    marginTop: 2,
    marginLeft: 8,
  },
  logHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  copyButton: {
    backgroundColor: '#607D8B',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
  },
  copyButtonText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  logContainer: {
    backgroundColor: '#1a1a1a',
    padding: 12,
    borderRadius: 8,
    maxHeight: 300,
  },
  logLine: {
    fontSize: 11,
    fontFamily: 'monospace',
    color: '#ddd',
    marginBottom: 2,
  },
  logError: {
    color: '#ff6b6b',
  },
  logWarning: {
    color: '#ffd93d',
  },
  footer: {
    height: 40,
  },
});

export default DualWriteTestScreen;
